// src/lib/master-engine.ts
// Zentrale Engine - NUR diese Datei darf rechnen

import {
  calculateDiagnosticScoring,
  mapWeakestDimensionsToPriorityTitles,
  type DiagnosticScoringResult,
  type DiagnosticDimension,
} from "@/lib/master-diagnostic-system";
import { calculateSalesRepPerformance } from "@/lib/salesRepPerformance";
import { calculateCompletenessScore } from "@/lib/completeness-score-calculator";

export type AuditAnswerMap = Record<string, unknown>;

export type EngineInput = {
  answers: Array<{ questionId: string; value: unknown }>;
  funnelData?: {
    monthlyContacts?: number | null;
    monthlyDiscoveryCalls?: number | null;
    monthlyPresentations?: number | null;
    monthlyDeals?: number | null;
    averageDealSize?: number | null;
    monthlyRevenue?: number | null;
    salesCycleMonths?: number;
    responseTime?: string;
    followUpDiscipline?: string;
    pipelineTransparency?: string;
    leadQuality?: string;
  };
  companyContext: {
    companyName?: string;
    industry?: string;
    salesMotion?: string;
    teamSize?: number;
    businessModel?: string;
    acquisitionMethods?: Record<string, number>;
  };
  // Original data for unknown field detection
  _originalFunnelData?: {
    monthlyContacts?: number;
    monthlyDiscoveryCalls?: number;
    monthlyPresentations?: number;
    monthlyDeals?: number;
    averageDealSize?: number;
    monthlyRevenue?: number;
    salesCycleMonths?: number;
    responseTime?: string;
    followUpDiscipline?: string;
    pipelineTransparency?: string;
    leadQuality?: string;
  };
  _originalCompanyProfile?: {
    teamSize?: string | number;
    [key: string]: unknown;
  };
};

export type MasterResult = {
  version: string;
  confidence: {
    overall: number;
    [key: string]: unknown;
  };
  questionsUsed: {
    core: number;
    context: number;
    action: number;
    answered: number;
  };
  categories: Array<{
    categoryId: string;
    percentage: number;
    [key: string]: unknown;
  }>;
  diagnostic: {
    categoryNeeds: unknown;
    operationalState: unknown;
    driverGaps: Array<{
      key: string;
      gap: number;
      [key: string]: unknown;
    }>;
  };
  simulation: {
    current: {
      contacts?: number;
      discoveryCalls?: number;
      presentations?: number;
      deals?: number;
      revenue?: number;
      salesCycleMonths?: number;
      [key: string]: unknown;
    };
    scenarios: {
      conservative: ScenarioResult;
      realistic: ScenarioResult;
      ambitious: ScenarioResult;
    };
  };
  benchmarks: {
    status?: string;
    [key: string]: unknown;
  } | null;
  levers: Array<{
    title?: string;
    name?: string;
    monthlyImpact?: number;
    annualImpact?: number;
    whyItMatters?: string;
    [key: string]: unknown;
  }>;
  risks: Array<{
    title?: string;
    level?: string;
    indicativeExposure?: number;
    [key: string]: unknown;
  }>;
  recommendations: Array<{
    id?: string;
    title: string;
    description?: string;
    type?: "quick_win" | "system_fix" | "structural_fix" | string;
    urgency?: string;
    timeToValue?: "fast" | "medium" | "slow" | string;
    linkedLeverTitle?: string | null;
    linkedLeverValue?: string | null;
    implementationHint?: string;
    evidence?: string[];
    expectedImpact?: string;
    [key: string]: unknown;
  }>;
  presentation: {
    hero?: unknown;
    executiveSummary?: unknown;
    diagnosticView?: unknown;
    funnelView?: unknown;
    benchmarkView?: unknown;
    leverView?: unknown;
    riskView?: unknown;
    recommendationView?: {
      quickWins: unknown[];
      systemFixes: unknown[];
      structuralFixes: unknown[];
      roadmap: {
        first30Days: unknown[];
        days31to60: unknown[];
        days61to90: unknown[];
      };
    };
    [key: string]: unknown;
  };
  notes: string[];
  
  // NEW: Direct data for UI (no more nested access)
  companyName?: string;
  dataGaps?: Array<{
    id: string;
    field: string;
    title: string;
    severity: "critical" | "warning" | "info";
    impact: string;
    recommendation: string;
    estimatedRevenueLoss: number;
  }>;
  topLevers?: Array<{
    title: string;
    monthlyImpact: number;
    annualImpact: number;
    whyItMatters: string;
    dimension?: DiagnosticDimension; // PRIO 2 FIX: Add dimension for causal mapping
  }>;
  completenessScore?: number;
  acquisitionMethods?: Record<string, number>;
  diagnosticScoring?: DiagnosticScoringResult;
  priorityTitles?: string[];

  // Structural causes for UI ("Why" layer)
  structuralCauses?: Array<{
    id: string;
    title: string;
    dimension: DiagnosticDimension | "REP_PERFORMANCE";
    severity: "high" | "medium" | "good";
    metricLabel?: string;
    metricValue?: string;
    summary: string;
    businessImpact: string;
    revenueImpactMonthly?: number;
    revenueImpactAnnual?: number;
  }>;

  // Operative Rep-Execution path (single source of truth for Block 5)
  repExecutionPath?: {
    confidenceScore: number;
    confidenceLabel: "hoch" | "mittel" | "niedrig";
    usesFallbackBenchmarks: boolean;
    productiveReps: number;
    // SOLL (ab jetzt pro Rep / Monat)
    monthlyRevenuePerRepTarget: number;
    monthlyDealsPerRepTarget: number;
    monthlyPresentationsPerRepTarget: number;
    monthlyDiscoveryCallsPerRepTarget: number;
    monthlyContactsPerRepTarget: number;
    // IST (aktuell pro Rep / Monat)
    currentRevenuePerRep: number;
    currentDealsPerRep: number;
    currentPresentationsPerRep: number;
    currentDiscoveryCallsPerRep: number;
    currentContactsPerRep: number;
  };

  // Focus priorities ("Where to act" layer — transition Analyse → Handlung)
  focusPriorities?: Array<{
    id: string;
    rank: 1 | 2 | 3;
    title: string;
    summary: string;
    rationale: string;
    priorityLabel: string;
    dimension: string;
  }>;

  // NEW: Normalized data (for calculations)
  normalizedData?: {
    companyName: string;
    industry: string;
    monthlyContacts: number;
    monthlyDiscoveryCalls: number;
    monthlyPresentations: number;
    monthlyDeals: number;
    averageDealSize: number;
    monthlyRevenue: number;
    salesCycleMonths: number;
    responseTime: string;
    followUpDiscipline: string;
    pipelineTransparency: string;
    leadQuality: string;
    teamSize: number;
  };
  
  // NEW: Computed metrics (for UI display)
  computedMetrics?: {
    convContactToDiscovery: number;
    convDiscoveryToPresentation: number;
    convPresentationToDeal: number;
    benchmarkContactToDiscovery: number;
    benchmarkDiscoveryToPresentation: number;
    benchmarkPresentationToDeal: number;
    currentRevenue: number;
    potentialRevenue: number;
    monthlyRevenueGap: number;
    annualRevenueGap: number;
    revenueGapSource?: "execution" | "forecast-delta" | "none";
    potentialDiscovery: number;
    potentialPresentations: number;
    potentialDeals: number;
    // NEW: Rep Performance Metrics (v2)
    repHealthScore?: number;
    absoluteGoalAttainmentPct?: number;
    expectedRevenueByNow?: number;
    pacingVsPlanPct?: number;
    paceGapToDate?: number;
    projectedYearEndRevenue?: number;
    projectedYearEndGap?: number;
    targetPerRepYear?: number;
    targetPerRepMonth?: number;
    actualRevenuePerRepYTD?: number;
    actualRevenuePerRepMonthPaced?: number;
    repTargetConsistencyGap?: number;
    pacingRecoveryPotentialMonthly?: number;
    repAlignmentPotentialMonthly?: number;
    pipelineActivationPotentialMonthly?: number;
    targetManagementScore?: number;
    pipelineReadinessScore?: number;
    teamExecutionScore?: number;
    // NEW: Goal Path - What's needed from NOW to reach year-end target
    goalPath?: {
      remainingRevenueNeeded: number;
      monthsRemainingApprox: number;
      requiredMonthlyRevenueFromNow: number;
      requiredDealsRemaining: number;
      requiredDealsPerMonthFromNow: number;
      requiredDealsPerRepPerMonthFromNow: number;
      requiredPresentationsRemaining: number;
      requiredPresentationsPerMonthFromNow: number;
      requiredPresentationsPerRepPerMonthFromNow: number;
      requiredDiscoveryCallsRemaining: number;
      requiredDiscoveryCallsPerMonthFromNow: number;
      requiredDiscoveryCallsPerRepPerMonthFromNow: number;
      requiredContactsRemaining: number;
      requiredContactsPerMonthFromNow: number;
      requiredContactsPerRepPerMonthFromNow: number;
      currentContactsPerRepPerMonth: number;
      currentDiscoveryCallsPerRepPerMonth: number;
      currentPresentationsPerRepPerMonth: number;
      currentDealsPerRepPerMonth: number;
      requiredContactToDiscoveryPctAtCurrentVolume: number | null;
      requiredDiscoveryToPresentationPctAtCurrentVolume: number | null;
      requiredPresentationToDealPctAtCurrentVolume: number | null;
      usesFallbackBenchmarks?: boolean;
      // v2: audit-adjustierte Raten + Confidence
      adjustedC2D?: number;
      adjustedD2P?: number;
      adjustedP2D?: number;
      confidence?: {
        score: number;
        label: string;
        factors: string[];
      };
      // v3: realistischer Pfad (audit-adjustierte Raten, kein doppelter Friction)
      realisticRequiredContactsPerMonthFromNow: number;
      realisticRequiredDiscoveryCallsPerMonthFromNow: number;
      realisticRequiredPresentationsPerMonthFromNow: number;
      realisticRequiredContactsPerRepPerMonthFromNow: number;
      realisticRequiredDiscoveryCallsPerRepPerMonthFromNow: number;
      realisticRequiredPresentationsPerRepPerMonthFromNow: number;
      // confidence shorthand
      confidenceScore: number;
      confidenceLabel: string;
      // aktuelle Ratios (für Diagnose & Vergleich)
      currentContactToDiscoveryRatio?: number;
      currentDiscoveryToPresentationRatio?: number;
      currentPresentationToDealRatio?: number;
      adjustedContactToDiscoveryRatio?: number;
      adjustedDiscoveryToPresentationRatio?: number;
      adjustedPresentationToDealRatio?: number;
    };
    capacityCheck?: {
      currentMaxRevenueCapacity: number;
      optimizedMaxRevenueCapacity: number;
      maxRevenueCapacity: number;
      capacityVsTargetPct: number;
      capacityGap: number;
      isTargetUnreachableWithCurrentSetup: boolean;
      isTargetUnreachableWithCurrentPerformance: boolean;
      currentDealsPerRepPerMonth: number;
      realisticDealsPerRepPerMonth: number;
      contactsPerRepPerMonth: number;
    };
    costInefficiency?: {
      totalSalaryCostYear: number;
      totalSalaryCostMonthly: number;
      inefficientSalaryCostYear: number;
      performanceRatio: number;
      revenuePerEuroSalary: number;
      revenueEfficiencyGapAnnual: number;
      targetRevenuePerEuroSalary: number;
      showBudgetEfficiencyWarning: boolean;
      salaryEfficiencyBand: "critical" | "low" | "solid" | "strong";
    };
    forecastSignal?: {
      isEarlyForecastLowConfidence: boolean;
      forecastConfidenceLabel: "low" | "medium" | "high";
    };
    optimizedContactToDiscovery?: number;
    optimizedDiscoveryToPresentation?: number;
    optimizedPresentationToDeal?: number;
    activityCheck?: {
      actualContactsPerRepPerMonth: number;
      requiredContactsPerRepFromNow: number;
      isActivityUnrealistic: boolean;
    };
    reverseFunnel?: {
      productiveReps: number;
      // Revenue
      targetMonthlyRevenue: number;
      targetMonthlyRevenuePerRep: number;
      istMonthlyRevenue: number;
      istMonthlyRevenuePerRep: number;
      // Team Totals — Soll
      targetDealsPerMonth: number;
      targetPresentationsPerMonth: number;
      targetDiscoveryCallsPerMonth: number;
      targetContactsPerMonth: number;
      // Per-Rep — Soll
      targetDealsPerRepPerMonth: number;
      targetPresentationsPerRepPerMonth: number;
      targetDiscoveryCallsPerRepPerMonth: number;
      targetContactsPerRepPerMonth: number;
      // Team Totals — Ist
      istContactsPerMonth: number;
      istDiscoveryCallsPerMonth: number;
      istPresentationsPerMonth: number;
      istDealsPerMonth: number;
      // Per-Rep — Ist
      istContactsPerRepPerMonth: number;
      istDiscoveryCallsPerRepPerMonth: number;
      istPresentationsPerRepPerMonth: number;
      istDealsPerRepPerMonth: number;
      // Gaps (Team)
      gapContacts: number;
      gapDiscovery: number;
      gapPresentations: number;
      gapDeals: number;
      gapRevenue: number;
      // Gaps (Per Rep)
      gapContactsPerRep: number;
      gapDiscoveryPerRep: number;
      gapPresentationsPerRep: number;
      gapDealsPerRep: number;
      hasEnoughData: boolean;
    };
    funnelDiagnosis?: {
      stages: Array<{
        name: string;
        actual: number;
        required: number;
        potential: number;
        gap: number;
        isBlocking: boolean;
      }>;
    };
    dataQuality?: {
      score: number;
      level: "high" | "medium" | "low";
      missingFields: string[];
    };
    conversionSimulator?: {
      baseContacts: number;
      baseC2D: number;
      baseD2P: number;
      baseP2D: number;
      avgDealSize: number;
      currentMonthlyRevenue: number;
    };
    salesCycleWarning?: {
      salesCycleMonths: number;
      isSalesCycleTooLong: boolean;
      dealsUnreachableDueToSalesCycle: number;
      monthsRemainingApprox: number;
    };
    contactActivitySignal?: {
      contactsPerRepPerMonth: number;
      level: "unknown" | "critical" | "low" | "ok" | "top";
      benchmarkMin: number;
      benchmarkTop: number;
      gap: number;
      gapPerTeam: number;
      monthlyRevenueLoss: number;
      annualRevenueLoss: number;
      isCritical: boolean;
    };
  };
  
  // NEW: Team Performance (detailed rep-level data)
  teamPerformance?: {
    productiveReps: number;
    totalRevenueTargetYear: number;
    revenueAchievedYTD: number;
    expectedRevenueByNow: number;
    absoluteGoalAttainmentPct: number;
    pacingVsPlanPct: number;
    paceGapToDate: number;
    projectedYearEndRevenue: number;
    projectedYearEndGap: number;
    targetPerRepYear: number;
    targetPerRepMonth: number;
    actualRevenuePerRepYTD: number;
    actualRevenuePerRepMonthPaced: number;
    targetManagementScore: number;
    pipelineReadinessScore: number;
    teamExecutionScore: number;
    repHealthScore: number;
    pacingRecoveryPotentialMonthly: number;
    repAlignmentPotentialMonthly: number;
    pipelineActivationPotentialMonthly: number;
    summarySignals: {
      hasPacingProblem: boolean;
      hasTargetBreakdownProblem: boolean;
      hasPipelineProblem: boolean;
      hasVarianceProblem: boolean;
      hasReviewProblem: boolean;
    };
    contactActivitySignal: {
      contactsPerRepPerMonth: number;
      level: "unknown" | "critical" | "low" | "ok" | "top";
      benchmarkMin: number;
      benchmarkTop: number;
      gap: number;
      gapPerTeam: number;
      monthlyRevenueLoss: number;
      annualRevenueLoss: number;
      isCritical: boolean;
    };
  };
  
  // Vereinheitlichte View-Layer für UI/PDF
  views: {
    executiveSummary: {
      title: string;
      mainMessage: string;
      confidenceLabel?: string;
      primaryFocus?: string;
      weakestCategories?: string[];
      topDrivers?: string[];
    };
    hero: {
      title: string;
      monthlyRevenueGap: number;
      annualRevenueGap: number;
      currentRevenue: number;
      realisticRevenue: number;
      conservativeRevenue: number;
      ambitiousRevenue: number;
      additionalRevenuePerMonth: number;
      rangeLabel: string;
      confidenceScore: number;
    };
    benchmarkView: unknown;
    riskView: {
      topRisks: unknown[];
      allRisks: unknown[];
    };
    leverView: {
      topLevers: unknown[];
      allLevers: unknown[];
    };
    recommendationView: {
      topPriorities: Array<{
        priority: number;
        title: string;
        description?: string;
        urgency?: string;
        linkedLeverTitle?: string | null;
        linkedLeverValue?: string | null;
        implementationHint?: string;
        type?: string;
        evidence?: string[];
      }>;
      quickWins: unknown[];
      systemFixes: unknown[];
      structuralFixes: unknown[];
      roadmap: {
        first30Days: unknown[];
        days31to60: unknown[];
        days61to90: unknown[];
      };
    };
    auditMeta: {
      completenessScore: number;
      answeredQuestions: number;
      totalQuestions: number;
      unknownFields: string[];
    };
  };
};

export type ScenarioResult = {
  current?: {
    revenue?: number;
    deals?: number;
    discoveryCalls?: number;
    presentations?: number;
    contacts?: number;
    [key: string]: unknown;
  };
  potential?: {
    revenue?: number;
    deals?: number;
    discoveryCalls?: number;
    presentations?: number;
    contacts?: number;
    [key: string]: unknown;
  };
  additionalRevenuePerMonth: number;
  [key: string]: unknown;
};

const safeNumber = (value: unknown, fallback = 0): number => {
  // null / undefined → use fallback (treat as "not provided")
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return n; // 0 is a valid value only if explicitly passed as 0 (not null)
};

// safeNumberOrNull: returns null if value is null/undefined/non-finite
const safeNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const formatCurrencyCompact = (value: number): string =>
  `${Math.round(value).toLocaleString("de-DE")} €`;

const buildUnknownFields = (
  answersMap: AuditAnswerMap,
  input: EngineInput,
): string[] => {
  const unknowns: string[] = [];
  const pushUnknown = (label: string, missing: boolean) => {
    if (missing) unknowns.push(label);
  };

  // Use original data if available, otherwise use processed data
  const originalFunnel = input._originalFunnelData;
  const originalProfile = input._originalCompanyProfile;

  // Helper to check if a numeric value is missing (null or undefined ONLY, 0 is valid!)
  const isMissingNumber = (value: unknown): boolean => {
    return value === null || value === undefined;
  };

  // Helper to check if a string value is "not-known"
  const isNotKnown = (value: unknown): boolean => {
    if (typeof value !== "string") return false;
    const str = value.toLowerCase().trim();
    return str === "not-known" || str === "nicht bekannt" || str === "unknown" || str === "";
  };

  // Helper: explizit als "Nicht bekannt" markiert (neues _unknown-Flag aus dem Wizard)
  const isExplicitlyUnknown = (flagKey: string): boolean => {
    return !!answersMap[flagKey];
  };

  // ── Kern-Funnel-Metriken ──────────────────────────────────────────
  pushUnknown(
    "Qualifizierte Kontaktaufnahmen",
    isMissingNumber(originalFunnel?.monthlyContacts) || isExplicitlyUnknown("monthlyContacts_unknown"),
  );
  pushUnknown(
    "Erstgespräche / Discovery Calls",
    isMissingNumber(originalFunnel?.monthlyDiscoveryCalls) || isExplicitlyUnknown("monthlyDiscoveryCalls_unknown"),
  );
  pushUnknown(
    "Präsentationen / Demos",
    isMissingNumber(originalFunnel?.monthlyPresentations) || isExplicitlyUnknown("monthlyPresentations_unknown"),
  );
  pushUnknown(
    "Abschlüsse pro Monat",
    isMissingNumber(originalFunnel?.monthlyDeals) || isExplicitlyUnknown("monthlyDeals_unknown"),
  );
  pushUnknown(
    "Durchschnittliche Deal-Größe",
    isMissingNumber(originalFunnel?.averageDealSize),
  );
  pushUnknown(
    "Sales Cycle",
    // Unbekannt wenn: kein Monatswert AND kein Range-Wert aus answersMap AND kein Wert im Funnel
    // ODER explizit als "Nicht bekannt" markiert
    (isMissingNumber(originalFunnel?.salesCycleMonths)
      && !answersMap.salesCycleRange)
    || isExplicitlyUnknown("salesCycleMonths_unknown"),
  );

  // ── Conversion-Raten ──────────────────────────────────────────────
  pushUnknown(
    "Conversion Lead → Discovery",
    isExplicitlyUnknown("convLeadToDiscoveryUnknown"),
  );
  pushUnknown(
    "Conversion Discovery → Präsentation",
    isExplicitlyUnknown("convDiscoveryToOfferUnknown"),
  );
  pushUnknown(
    "Conversion Angebot → Abschluss",
    isExplicitlyUnknown("convOfferToCloseUnknown"),
  );

  // ── Qualitative Felder ────────────────────────────────────────────
  pushUnknown(
    "Reaktionszeit",
    !originalFunnel?.responseTime || isNotKnown(originalFunnel?.responseTime),
  );
  pushUnknown(
    "Follow-up-Disziplin",
    !originalFunnel?.followUpDiscipline || isNotKnown(originalFunnel?.followUpDiscipline),
  );
  pushUnknown(
    "Pipeline-Transparenz",
    !originalFunnel?.pipelineTransparency || isNotKnown(originalFunnel?.pipelineTransparency),
  );
  pushUnknown(
    "Lead-Qualität",
    !originalFunnel?.leadQuality || isNotKnown(originalFunnel?.leadQuality),
  );
  pushUnknown(
    "Teamgröße",
    isMissingNumber(originalProfile?.teamSize),
  );

  return unknowns;
};

// PRIO 4 FIX: Removed old buildCompletenessScore - now using calculateCompletenessScore from completeness-score-calculator.ts

// -------------------------------------------------------
// HELPER FUNCTIONS FOR REP PERFORMANCE V2
// -------------------------------------------------------

function mapRepsOnTrack(option?: string): number {
  switch (option) {
    case "almost-all":
      return 0.85;
    case "more-than-half":
      return 0.6;
    case "less-than-half":
      return 0.35;
    case "hardly-any":
      return 0.1;
    default:
      return 0;
  }
}

function mapTargetBreakdown(option?: string): number {
  switch (option) {
    case "yes":
      return 90;
    case "partial":
      return 60;
    case "no":
      return 25;
    default:
      return 50;
  }
}

function mapPipelineCoverageReality(option?: string): number {
  switch (option) {
    case "almost-all":
      return 85;
    case "more-than-half":
      return 60;
    case "less-than-half":
      return 35;
    case "hardly-any":
      return 10;
    default:
      return 0;
  }
}

function mapPipelineConfidence(option?: string): number {
  switch (option) {
    case "very-high":
      return 85;
    case "rather-high":
      return 65;
    case "rather-low":
      return 40;
    case "very-low":
      return 20;
    default:
      return 0;
  }
}

// mapDealMomentum removed — dealMomentumLoss field was removed from wizard.
// Weight redistributed to pipelineReadinessScore.
function mapReviewCadence(option?: string): number {
  switch (option) {
    case "weekly":
      return 95;
    case "biweekly":
      return 80;
    case "monthly":
      return 65;
    case "rarely":
      return 35;
    case "never":
      return 10;
    default:
      return 50;
  }
}

// -------------------------------------------------------
// REP EXECUTION PATH BUILDER
// -------------------------------------------------------
function buildRepExecutionPath(params: {
  productiveReps: number;
  avgDealSize: number;
  currentMonthlyDeals: number;
  currentMonthlyPresentations: number;
  currentMonthlyDiscoveryCalls: number;
  currentMonthlyContacts: number;
  currentMonthlyRevenue: number;
  requiredMonthlyRevenueFromNow: number;
  annualRevenueTarget: number;   // NEU: Jahresziel als Fallback
  // Adjustierte Conversion-Raten (Pfad B) — als Ratios (z.B. 0.25 für 25%)
  adjustedP2DRatio: number;
  adjustedD2PRatio: number;
  adjustedC2DRatio: number;
  confidenceScore: number;
  usesFallbackBenchmarks: boolean;
}): NonNullable<MasterResult['repExecutionPath']> {
  const reps = Math.max(1, params.productiveReps);
  const dealSize = Math.max(1, params.avgDealSize);

  // IST pro Rep / Monat
  const currentRevenuePerRep  = params.currentMonthlyRevenue / reps;
  const currentDealsPerRep    = params.currentMonthlyDeals / reps;
  const currentPresPerRep     = params.currentMonthlyPresentations / reps;
  const currentDiscPerRep     = params.currentMonthlyDiscoveryCalls / reps;
  const currentContPerRep     = params.currentMonthlyContacts / reps;

  // Monats-Zielumsatz pro Rep:
  // Wenn requiredMonthlyRevenueFromNow > 0  → struktureller Rückstand, direkt verwenden
  // Wenn 0 (Ziel bereits übertroffen)       → Ziel = Jahresziel / 12, damit die Kette sinnvoll bleibt
  const baseMonthlyRevTarget =
    params.requiredMonthlyRevenueFromNow > 0
      ? params.requiredMonthlyRevenueFromNow
      : params.annualRevenueTarget / 12;
  const revenueTarget = baseMonthlyRevTarget / reps;

  // Deal-Ziel pro Rep (Reverse-Funnel-Startpunkt)
  const dealsTargetRaw = dealSize > 0 ? revenueTarget / dealSize : 0;

  // Soll muss immer >= Ist sein — sonst ist kein Gap vorhanden
  // (passiert z.B. wenn Ist-Umsatz bereits über dem hochgerechneten Monatsziel liegt)
  const dealsTarget = Math.max(dealsTargetRaw, currentDealsPerRep);

  // Konsistente Rückrechnung: Deals → Präsentationen → Discovery → Kontakte
  const presTarget = params.adjustedP2DRatio > 0 ? dealsTarget / params.adjustedP2DRatio : 0;
  const discTarget = params.adjustedD2PRatio > 0 ? presTarget  / params.adjustedD2PRatio : 0;
  const contTarget = params.adjustedC2DRatio > 0 ? discTarget  / params.adjustedC2DRatio : 0;

  return {
    confidenceScore: params.confidenceScore,
    confidenceLabel:
      params.confidenceScore >= 80 ? "hoch" :
      params.confidenceScore >= 60 ? "mittel" : "niedrig",
    usesFallbackBenchmarks: params.usesFallbackBenchmarks,
    productiveReps: reps,

    // SOLL pro Rep / Monat — vollständig konsistente Kette
    monthlyRevenuePerRepTarget: revenueTarget,
    monthlyDealsPerRepTarget: dealsTarget,
    monthlyPresentationsPerRepTarget: presTarget,
    monthlyDiscoveryCallsPerRepTarget: discTarget,
    monthlyContactsPerRepTarget: contTarget,

    // IST pro Rep / Monat
    currentRevenuePerRep,
    currentDealsPerRep,
    currentPresentationsPerRep: currentPresPerRep,
    currentDiscoveryCallsPerRep: currentDiscPerRep,
    currentContactsPerRep: currentContPerRep,
  };
}

// -------------------------------------------------------
// FOCUS PRIORITIES BUILDER
// -------------------------------------------------------
function buildFocusPriorities(params: {
  topLevers: Array<{ title: string; monthlyImpact: number; annualImpact: number; whyItMatters: string; dimension?: DiagnosticDimension; }>;
  diagnosticScoring: DiagnosticScoringResult;
}): NonNullable<MasterResult['focusPriorities']> {
  const { topLevers, diagnosticScoring } = params;

  type FocusMap = {
    title: string;
    summary: string;
    rationale: string;
    priorityLabel: string;
  };

  const DIMENSION_FOCUS_MAP: Record<string, FocusMap> = {
    DEMAND: {
      title: "Pipeline-Aufbau stabilisieren",
      summary: "Ihre aktuelle Nachfrage reicht nicht aus, um Ihr Ziel zuverlässig zu erreichen. Ohne zusätzlichen Input wird sich die Lücke nicht schließen.",
      rationale: "Höchste Priorität",
      priorityLabel: "Größter Hebel",
    },
    CONVERSION: {
      title: "Conversion im Closing verbessern",
      summary: "Ein signifikanter Teil Ihres Umsatzpotenzials geht in der letzten Phase verloren. Schon kleine Verbesserungen haben hier unmittelbar große Wirkung.",
      rationale: "Schnellster Hebel",
      priorityLabel: "Schnellster Hebel",
    },
    PROCESS: {
      title: "Prozessdisziplin erhöhen",
      summary: "Deals verlieren Momentum und bleiben zu lange im Funnel. Das verzerrt Ihre Forecasts und reduziert Abschlussraten systematisch.",
      rationale: "Stabilisiert Ihre Pipeline",
      priorityLabel: "Stabilisiert Pipeline",
    },
    SALES_QUALITY: {
      title: "Discovery & Qualifizierung schärfen",
      summary: "Zu viele Opportunities gelangen in den Prozess, die nicht kaufreif sind oder später unnötig verloren gehen. Bessere Qualifizierung spart Aufwand und verbessert Closing.",
      rationale: "Verbessert Conversion nachhaltig",
      priorityLabel: "Nachhaltigster Hebel",
    },
    REP_PERFORMANCE: {
      title: "Team Execution gleichmäßiger machen",
      summary: "Die Performance-Streuung im Team kostet planbares Wachstum. Wenn der Durchschnitt auf das Niveau der besten Reps gehoben wird, steigt der Gesamtumsatz deutlich.",
      rationale: "Hebt das gesamte Team",
      priorityLabel: "Multiplikator-Effekt",
    },
    SALES_CYCLE: {
      title: "Sales Cycle aktiv verkürzen",
      summary: "Zu lange Zyklen binden Kapazität, verzerren Forecasts und erhöhen das Risiko, dass Deals einfrieren. Mehr Deals könnten noch dieses Jahr abgeschlossen werden.",
      rationale: "Mehr Deals noch dieses Jahr",
      priorityLabel: "Sofort-Wirkung",
    },
  };

  // Use topLevers (sorted by impact) as the ranking source
  // Fill missing dimensions from weakestDimensionsRanked if fewer than 3 levers
  const seen = new Set<string>();
  const ranked: Array<{ dimension: string; monthlyImpact: number }> = [];

  // First pass: levers with known dimension
  for (const lever of topLevers) {
    if (lever.dimension && !seen.has(lever.dimension)) {
      seen.add(lever.dimension);
      ranked.push({ dimension: lever.dimension, monthlyImpact: lever.monthlyImpact });
    }
  }

  // Second pass: fill from weakest dimensions if needed
  const weakest: string[] = diagnosticScoring.weakestDimensionsRanked || [];
  for (const dim of weakest) {
    if (ranked.length >= 3) break;
    if (!seen.has(dim) && DIMENSION_FOCUS_MAP[dim]) {
      seen.add(dim);
      ranked.push({ dimension: dim, monthlyImpact: 0 });
    }
  }

  // Fallback: ensure we always have 3 entries
  const FALLBACK_ORDER = ['DEMAND', 'CONVERSION', 'PROCESS', 'SALES_QUALITY', 'REP_PERFORMANCE', 'SALES_CYCLE'];
  for (const dim of FALLBACK_ORDER) {
    if (ranked.length >= 3) break;
    if (!seen.has(dim)) {
      seen.add(dim);
      ranked.push({ dimension: dim, monthlyImpact: 0 });
    }
  }

  return ranked.slice(0, 3).map((entry, idx) => {
    const map = DIMENSION_FOCUS_MAP[entry.dimension] ?? {
      title: "Fokusbereich optimieren",
      summary: "Dieser Bereich hat signifikantes Verbesserungspotenzial für Ihre Umsatzentwicklung.",
      rationale: "Relevanter Hebel",
      priorityLabel: "Wichtiger Hebel",
    };
    return {
      id: `focus-${entry.dimension.toLowerCase().replace('_', '-')}-${idx + 1}`,
      rank: (idx + 1) as 1 | 2 | 3,
      title: map.title,
      summary: map.summary,
      rationale: map.rationale,
      priorityLabel: map.priorityLabel,
      dimension: entry.dimension,
    };
  });
}

// -------------------------------------------------------
// STRUCTURAL CAUSES BUILDER
// -------------------------------------------------------
function buildStructuralCauses(params: {
  diagnosticScoring: DiagnosticScoringResult;
  monthlyRevenueGap: number;
  annualRevenueGap: number;
  topLevers: Array<{ title: string; monthlyImpact: number; annualImpact: number; whyItMatters: string; dimension?: DiagnosticDimension; }>;
  convPresentationToDeal: number;
  benchmarkPresentationToDeal: number;
  reverseFunnel?: { gapContacts: number; istContactsPerRepPerMonth: number; targetContactsPerRepPerMonth: number; } | null;
  salesCycleWarning?: { salesCycleMonths: number; isSalesCycleTooLong: boolean; } | null;
  rawAnswers: Record<string, unknown>;
  teamPerformance?: { productiveReps: number; repHealthScore: number; projectedYearEndGap: number; actualRevenuePerRepMonthPaced: number; targetPerRepMonth: number; } | null;
}): MasterResult['structuralCauses'] {
  const {
    diagnosticScoring, monthlyRevenueGap, annualRevenueGap, topLevers,
    convPresentationToDeal, benchmarkPresentationToDeal,
    reverseFunnel, salesCycleWarning, rawAnswers, teamPerformance,
  } = params;

  const weakest: string[] = diagnosticScoring.weakestDimensionsRanked || [];

  const getNum = (key: string): number | null => {
    const v = rawAnswers?.[key];
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const stalePipelineShare = getNum('stalePipelineSharePercentExact');
  const proposalsWithNextStep = getNum('proposalsWithNextStepPercentExact');
  const decisionMakerInFirstMeeting = getNum('decisionMakerInFirstMeetingPercentExact');
  const avgFollowUps = getNum('avgFollowUpsPerLeadExact');
  const qualifiedLeadRate = getNum('qualifiedLeadRatePercentExact');

  const leverByDim = (dim: string) =>
    topLevers.find((l) => (l.dimension as string) === dim);

  type Cause = NonNullable<MasterResult['structuralCauses']>[number] & { sortScore: number };
  const causes: Cause[] = [];

  // --- DEMAND ---
  if (weakest.includes('DEMAND')) {
    const current = reverseFunnel?.istContactsPerRepPerMonth || 0;
    const target = reverseFunnel?.targetContactsPerRepPerMonth || 0;
    const gapPct = target > 0 ? ((target - current) / target) * 100 : 0;
    const lever = leverByDim('DEMAND');
    causes.push({
      id: 'cause-demand',
      title: 'Ihre Pipeline ist strukturell zu klein',
      dimension: 'DEMAND',
      severity: gapPct >= 25 ? 'high' : 'medium',
      metricLabel: 'Kontaktlücke pro Rep / Monat',
      metricValue: target > 0 ? `${Math.max(0, Math.round(target - current))} Kontakte fehlen` : undefined,
      summary: 'Die aktuelle Nachfragebasis reicht nicht stabil aus, um Ihr Umsatzziel mit der bestehenden Funnel-Logik planbar zu erreichen.',
      businessImpact: gapPct >= 25
        ? 'Schon vor dem eigentlichen Verkaufsgespräch fehlen Ihnen genügend qualifizierte Chancen. Dadurch steigt der Druck auf Conversion und Forecast.'
        : 'Ihre Nachfragebasis ist zu knapp. Kleine Schwankungen in Lead-Qualität oder Conversion schlagen dadurch sofort auf die Zielerreichung durch.',
      revenueImpactMonthly: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.32),
      revenueImpactAnnual: lever?.annualImpact ?? Math.round(annualRevenueGap * 0.32),
      sortScore: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.32),
    });
  }

  // --- CONVERSION ---
  if (weakest.includes('CONVERSION')) {
    const gapPct = benchmarkPresentationToDeal > 0
      ? ((benchmarkPresentationToDeal - convPresentationToDeal) / benchmarkPresentationToDeal) * 100
      : 0;
    const lever = leverByDim('CONVERSION');
    causes.push({
      id: 'cause-conversion',
      title: 'Ihre Conversion ist zu schwach für das Zielniveau',
      dimension: 'CONVERSION',
      severity: gapPct >= 20 ? 'high' : 'medium',
      metricLabel: 'Presentation → Deal',
      metricValue: `${convPresentationToDeal.toFixed(1)}% vs. Benchmark ${benchmarkPresentationToDeal.toFixed(1)}%`,
      summary: 'Zwischen Gespräch, Präsentation und Abschluss entstehen zu viele Verluste. Dadurch müssen Sie deutlich mehr Opportunities erzeugen, um denselben Umsatz zu erreichen.',
      businessImpact: 'Ein schwacher Funnel verteuert Wachstum: Mehr Leads, mehr Gespräche und mehr Vertriebsaufwand sind nötig, obwohl der eigentliche Engpass später im Prozess entsteht.',
      revenueImpactMonthly: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.28),
      revenueImpactAnnual: lever?.annualImpact ?? Math.round(annualRevenueGap * 0.28),
      sortScore: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.28),
    });
  }

  // --- PROCESS ---
  if (weakest.includes('PROCESS')) {
    const lever = leverByDim('PROCESS');
    const staleLabel = stalePipelineShare !== null ? `${Math.round(stalePipelineShare)}% stagnierende Pipeline` : undefined;
    causes.push({
      id: 'cause-process',
      title: 'Ihr Sales-Prozess ist nicht konsequent genug geführt',
      dimension: 'PROCESS',
      severity: (stalePipelineShare ?? 0) > 25 || (proposalsWithNextStep ?? 100) < 60 ? 'high' : 'medium',
      metricLabel: 'Prozesssignal',
      metricValue: staleLabel ?? (proposalsWithNextStep !== null ? `${Math.round(proposalsWithNextStep)}% Angebote mit Next Step` : undefined),
      summary: 'Fehlende Next Steps, geringe Verbindlichkeit und zu wenig Bewegung in der Pipeline machen die Performance instabil und schwer steuerbar.',
      businessImpact: 'Deals bleiben liegen, Forecasts werden ungenau und Ihr Team arbeitet zu oft an Chancen ohne klaren Fortschritt.',
      revenueImpactMonthly: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.22),
      revenueImpactAnnual: lever?.annualImpact ?? Math.round(annualRevenueGap * 0.22),
      sortScore: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.22),
    });
  }

  // --- SALES_QUALITY ---
  if (weakest.includes('SALES_QUALITY')) {
    const lever = leverByDim('SALES_QUALITY');
    causes.push({
      id: 'cause-sales-quality',
      title: 'Discovery und Qualifizierung greifen noch nicht konsequent genug',
      dimension: 'SALES_QUALITY',
      severity: (decisionMakerInFirstMeeting ?? 100) < 50 || (avgFollowUps ?? 10) < 3 ? 'high' : 'medium',
      metricLabel: 'Qualitätssignal',
      metricValue: decisionMakerInFirstMeeting !== null
        ? `${Math.round(decisionMakerInFirstMeeting)}% Entscheider im Erstgespräch`
        : qualifiedLeadRate !== null ? `${Math.round(qualifiedLeadRate)}% qualifizierte Leads` : undefined,
      summary: 'Bedarfstiefe, Buying-Center-Einbindung und konsequente Nachverfolgung sind noch nicht stark genug standardisiert.',
      businessImpact: 'Dadurch gelangen zu viele Opportunities in den Prozess, die entweder nicht kaufreif sind oder später unnötig verloren gehen.',
      revenueImpactMonthly: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.20),
      revenueImpactAnnual: lever?.annualImpact ?? Math.round(annualRevenueGap * 0.20),
      sortScore: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.20),
    });
  }

  // --- REP PERFORMANCE ---
  if (teamPerformance && teamPerformance.productiveReps > 0) {
    const repGap = teamPerformance.targetPerRepMonth > 0
      ? ((teamPerformance.targetPerRepMonth - teamPerformance.actualRevenuePerRepMonthPaced) / teamPerformance.targetPerRepMonth) * 100
      : 0;
    if (repGap > 10 || teamPerformance.repHealthScore < 70 || teamPerformance.projectedYearEndGap > 0) {
      const lever = leverByDim('REP_PERFORMANCE');
      causes.push({
        id: 'cause-rep-performance',
        title: 'Die aktuelle Rep-Performance ist nicht stabil genug auf Zielkurs',
        dimension: 'REP_PERFORMANCE',
        severity: repGap >= 20 ? 'high' : 'medium',
        metricLabel: 'Revenue pro Rep / Monat',
        metricValue: `${Math.round(teamPerformance.actualRevenuePerRepMonthPaced).toLocaleString('de-DE')} € vs. Ziel ${Math.round(teamPerformance.targetPerRepMonth).toLocaleString('de-DE')} €`,
        summary: 'Das Team liefert aktuell nicht durchgängig auf dem Niveau, das für eine sichere Zielerreichung nötig wäre.',
        businessImpact: 'Ein Teil des Umsatzrückstands ist kein reines Nachfrageproblem, sondern entsteht durch Pace, Zielklarheit und ungleichmäßige Umsetzung im Team.',
        revenueImpactMonthly: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.24),
        revenueImpactAnnual: lever?.annualImpact ?? Math.round(annualRevenueGap * 0.24),
        sortScore: lever?.monthlyImpact ?? Math.round(monthlyRevenueGap * 0.24),
      });
    }
  }

  // --- SALES CYCLE ---
  if (salesCycleWarning?.isSalesCycleTooLong) {
    causes.push({
      id: 'cause-sales-cycle',
      title: 'Ihre Deals bewegen sich zu langsam durch den Funnel',
      dimension: 'PROCESS',
      severity: 'medium',
      metricLabel: 'Sales Cycle',
      metricValue: `${salesCycleWarning.salesCycleMonths.toFixed(1)} Monate`,
      summary: 'Die Dauer bis zum Abschluss bindet Pipeline und verlangsamt die Umsatzrealisierung spürbar.',
      businessImpact: 'Selbst bei ausreichender Nachfrage wird Umsatz später realisiert als nötig. Das schwächt Forecast, Liquidität und die Chance, Lücken im laufenden Jahr aufzuholen.',
      revenueImpactMonthly: Math.round(monthlyRevenueGap * 0.14),
      revenueImpactAnnual: Math.round(annualRevenueGap * 0.14),
      sortScore: Math.round(monthlyRevenueGap * 0.14),
    });
  }

  return causes
    .sort((a, b) => b.sortScore - a.sortScore)
    .slice(0, 5)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    .map(({ sortScore: _s, ...cause }) => cause);
}

// -------------------------------------------------------
// MAIN ENGINE
// -------------------------------------------------------
// Simplified engine - calculates basic metrics without V3 dependency
export function buildMasterResult(input: EngineInput): MasterResult {
  const funnel = input.funnelData || {};
  const companyContext = input.companyContext || {};

  // answersMap needed early for conversion fallback logic
  const answersMap = Object.fromEntries(input.answers.map(a => [a.questionId, a.value]));

  // -------------------------------------------------------
  // CONVERSION RATES — SMART FALLBACK CHAIN
  // Priority 1: calculated from absolute monthly volumes (most reliable)
  // Priority 2: directly entered % from Step 1 (exact input > range midpoint)
  // Priority 3: 0 / benchmark used later
  // -------------------------------------------------------

  // Direct inputs from Step 1 (already resolved to numeric midpoint in Wrapper)
  const directC2D = Number(answersMap.convLeadToDiscoveryExact) > 0
    ? Number(answersMap.convLeadToDiscoveryExact) : null;
  const directD2P = Number(answersMap.convDiscoveryToOfferExact) > 0
    ? Number(answersMap.convDiscoveryToOfferExact) : null;
  const directP2D = Number(answersMap.convOfferToCloseExact) > 0
    ? Number(answersMap.convOfferToCloseExact) : null;

  // Absolute monthly volumes from Step 0
  const rawContacts      = funnel.monthlyContacts      != null ? Number(funnel.monthlyContacts)      : null;
  const rawDiscovery     = funnel.monthlyDiscoveryCalls != null ? Number(funnel.monthlyDiscoveryCalls) : null;
  const rawPresentations = funnel.monthlyPresentations  != null ? Number(funnel.monthlyPresentations)  : null;
  const rawDeals         = funnel.monthlyDeals          != null ? Number(funnel.monthlyDeals)          : null;

  const avgDealSize    = safeNumber(funnel.averageDealSize, 10000);
  const currentRevenue = safeNumber(funnel.monthlyRevenue,
    rawDeals != null ? rawDeals * avgDealSize : 0);

  // -------------------------------------------------------
  // Fallback reconstruction: wenn Volumen-Stufe fehlt, aus direkten % + bekannten Stufen ableiten
  // -------------------------------------------------------

  // Deals sind immer Pflichtfeld (Step 0) – als Anker verwenden
  const currentDeals = rawDeals != null && rawDeals > 0 ? rawDeals : 0;

  // Presentations: aus Volumen ODER aus Deals / P2D-Rate
  const currentPresentations: number = (() => {
    if (rawPresentations != null && rawPresentations > 0) return rawPresentations;
    if (currentDeals > 0 && directP2D != null && directP2D > 0)
      return Math.round(currentDeals / (directP2D / 100));
    return 0;
  })();

  // Discovery: aus Volumen ODER aus Presentations / D2P-Rate
  const currentDiscovery: number = (() => {
    if (rawDiscovery != null && rawDiscovery > 0) return rawDiscovery;
    if (currentPresentations > 0 && directD2P != null && directD2P > 0)
      return Math.round(currentPresentations / (directD2P / 100));
    return 0;
  })();

  // Contacts: aus Volumen ODER aus Discovery / C2D-Rate
  const currentContacts: number = (() => {
    if (rawContacts != null && rawContacts > 0) return rawContacts;
    if (currentDiscovery > 0 && directC2D != null && directC2D > 0)
      return Math.round(currentDiscovery / (directC2D / 100));
    return 0;
  })();

  // -------------------------------------------------------
  // FINAL CONVERSION RATES
  // Priority: calculated from volumes > direct input > 0
  // -------------------------------------------------------
  const calcC2D = currentContacts > 0 && currentDiscovery >= 0
    ? Math.max(0, Math.min(100, (currentDiscovery / currentContacts) * 100)) : null;
  const calcD2P = currentDiscovery > 0 && currentPresentations >= 0
    ? Math.max(0, Math.min(100, (currentPresentations / currentDiscovery) * 100)) : null;
  const calcP2D = currentPresentations > 0 && currentDeals >= 0
    ? Math.max(0, Math.min(100, (currentDeals / currentPresentations) * 100)) : null;

  // Use calculated first, then direct input as fallback
  const currentC2D = calcC2D ?? directC2D ?? 0;
  const currentD2P = calcD2P ?? directD2P ?? 0;
  const currentP2D = calcP2D ?? directP2D ?? 0;

  // Flags: was this rate derived from real volume data?
  const hasRealContactToDiscovery     = calcC2D !== null || (directC2D !== null && currentDiscovery > 0);
  const hasRealDiscoveryToPresentation = calcD2P !== null || (directD2P !== null && currentPresentations > 0);
  const hasRealPresentationToDeal      = calcP2D !== null || (directP2D !== null && currentDeals > 0);

  // Legacy aliases (used downstream in conversions output)
  const convContactToDiscovery      = currentC2D;
  const convDiscoveryToPresentation = currentD2P;
  const convPresentationToDeal      = currentP2D;

  // -------------------------------------------------------
  // BENCHMARKS — realistische B2B-Werte
  // C2D: Kontakt → Discovery Call = 3–8 % (gut: ~6 %)
  // D2P: Discovery → Präsentation  = 40–60 % (gut: ~50 %)
  // P2D: Präsentation → Abschluss  = 20–35 % (gut: ~28 %)
  // -------------------------------------------------------
  const benchmarkContactToDiscovery = 6;       // realistisch B2B-Kaltakquise/Warm-Outbound
  const benchmarkDiscoveryToPresentation = 50; // wenn Qualifizierung stimmt
  const benchmarkPresentationToDeal = 28;      // gut geführte B2B-Deals

  // Improvement corridors only apply when below benchmark
  // Potential is NEVER below current actual performance
  const improvedC2D =
    !hasRealContactToDiscovery
      ? benchmarkContactToDiscovery
      : currentC2D >= benchmarkContactToDiscovery
      ? currentC2D
      : Math.min(benchmarkContactToDiscovery, currentC2D + 8);
  const improvedD2P =
    !hasRealDiscoveryToPresentation
      ? benchmarkDiscoveryToPresentation
      : currentD2P >= benchmarkDiscoveryToPresentation
      ? currentD2P
      : Math.min(benchmarkDiscoveryToPresentation, currentD2P + 10);
  const improvedP2D =
    !hasRealPresentationToDeal
      ? benchmarkPresentationToDeal
      : currentP2D >= benchmarkPresentationToDeal
      ? currentP2D
      : Math.min(benchmarkPresentationToDeal, currentP2D + 8);

  // "Benchmark view" for UI = realistic optimized state, not hard market benchmark
  const potentialContactToDiscovery = improvedC2D;
  const potentialDiscoveryToPresentation = improvedD2P;
  const potentialPresentationToDeal = improvedP2D;

  // -------------------------------------------------------
  // REALISTIC SCENARIOS
  // Strategy:
  //   A) If Contacts are known  → full top-down funnel calculation
  //   B) If Contacts = 0/unknown → deal-anchored fallback:
  //        use current deals as baseline, apply P2D improvement
  //        (and D2P improvement from presentations if known)
  // This prevents gap = 0 when only funnel volume is missing.
  // -------------------------------------------------------

  const hasContactData = currentContacts > 0;
  const hasPresentationData = currentPresentations > 0;
  const hasDiscoveryData = currentDiscovery > 0;

  // --- Full top-down path (A) ---
  const realisticDiscoveryFull = (currentContacts * improvedC2D) / 100;
  const realisticPresentationsFull = (realisticDiscoveryFull * improvedD2P) / 100;
  const realisticDealsFull = (realisticPresentationsFull * improvedP2D) / 100;

  // --- Deal-anchored fallback path (B) ---
  // When contacts are unknown, we anchor to current deals and estimate realistic uplift.
  // KEY INSIGHT: The revenue gap must reflect that same deals can come from a more
  // efficient funnel. We use two strategies:
  //   B1. If real conversion rates are known → ratio-based improvement
  //   B2. If rates are unknown → assume benchmark efficiency gains of 25-35%
  //       This is conservative but avoids a 0-gap when only volume is missing.

  // Step B: back-calculate presentations from deals using KNOWN or benchmark P2D
  const impliedCurrentP2D = hasPresentationData && currentPresentations > 0
    ? (currentDeals / currentPresentations) * 100
    : (hasRealPresentationToDeal ? currentP2D : benchmarkPresentationToDeal);

  const dealAnchoredPresentations = hasPresentationData
    ? currentPresentations
    : currentDeals / Math.max(0.01, impliedCurrentP2D / 100);

  const impliedCurrentD2P = hasDiscoveryData && dealAnchoredPresentations > 0
    ? (dealAnchoredPresentations / currentDiscovery) * 100
    : (hasRealDiscoveryToPresentation ? currentD2P : benchmarkDiscoveryToPresentation);

  const dealAnchoredDiscovery = hasDiscoveryData
    ? currentDiscovery
    : dealAnchoredPresentations / Math.max(0.01, impliedCurrentD2P / 100);

  // B1: Rate-known path — scale deals by P2D improvement ratio
  const rateKnownRealisticDeals = hasPresentationData
    ? (currentPresentations * improvedP2D) / 100
    : currentDeals * (improvedP2D / Math.max(0.1, impliedCurrentP2D));

  // B2: Rate-unknown path — apply a conservative 25% efficiency uplift on current deals
  // This represents achievable gains from better follow-up, qualification, and process.
  const CONSERVATIVE_UPLIFT_FACTOR = 1.25; // 25% more deals with better execution
  const rateUnknownRealisticDeals = currentDeals * CONSERVATIVE_UPLIFT_FACTOR;

  // Choose B1 if we have real conversion data, else blend with B2 floor
  const hasAnyRealRate = hasRealPresentationToDeal || hasRealDiscoveryToPresentation || hasPresentationData || hasDiscoveryData;
  const realisticDealsAnchoredP2D = hasAnyRealRate
    ? Math.max(rateKnownRealisticDeals, currentDeals) // rate-based, but no worse than current
    : Math.max(rateUnknownRealisticDeals, currentDeals * 1.1); // unknown rates → use uplift

  // Improved presentations
  const realisticPresentationsAnchored = hasDiscoveryData
    ? (currentDiscovery * improvedD2P) / 100
    : dealAnchoredDiscovery > 0
    ? (dealAnchoredDiscovery * improvedD2P) / 100
    : dealAnchoredPresentations;

  const realisticDealsAnchored = Math.max(
    realisticDealsAnchoredP2D,
    (realisticPresentationsAnchored * improvedP2D) / 100,
    currentDeals // hard floor
  );

  // ── Choose strategy ──
  // If contacts are known AND produce a better realistic result → use full path
  // Otherwise fall back to deal-anchored path
  const realisticDiscovery = hasContactData ? realisticDiscoveryFull : dealAnchoredDiscovery;
  const realisticPresentations = hasContactData ? realisticPresentationsFull : realisticPresentationsAnchored;
  const realisticDeals = hasContactData
    ? realisticDealsFull
    : realisticDealsAnchored;
  const realisticRevenue = realisticDeals * avgDealSize;

  const conservativeDiscovery = currentDiscovery + (realisticDiscovery - currentDiscovery) * 0.5;
  const conservativePresentations =
    currentPresentations + (realisticPresentations - currentPresentations) * 0.5;
  const conservativeDeals = currentDeals + (realisticDeals - currentDeals) * 0.5;
  const conservativeRevenue = conservativeDeals * avgDealSize;

  // Ambitious = only upside extension if realistic is above current
  const ambitiousDiscovery =
    realisticDiscovery > currentDiscovery
      ? currentDiscovery + (realisticDiscovery - currentDiscovery) * 1.25
      : currentDiscovery;
  const ambitiousPresentations =
    realisticPresentations > currentPresentations
      ? currentPresentations + (realisticPresentations - currentPresentations) * 1.25
      : currentPresentations;
  const ambitiousDeals =
    realisticDeals > currentDeals
      ? currentDeals + (realisticDeals - currentDeals) * 1.25
      : currentDeals;
  const ambitiousRevenue = ambitiousDeals * avgDealSize;

  // ── Revenue Gap: Execution-basiert wenn Kontaktdaten bekannt,
  //    sonst aus dem Forecast-Delta (Ziel vs. hochgerechneter YTD-Pace).
  //    Das ist ehrlicher als ein pauschaler Uplift-Faktor.
  const executionBasedMonthlyGap = Math.max(0, realisticRevenue - currentRevenue);

  // Forecast-Delta Fallback:
  // Nutzt projectedYearEndGap (= Hochrechnung aus tatsächlichem YTD) geteilt durch
  // verbleibende Monate — NICHT annualCurrentPace (= aktueller Monatsumsatz × 12).
  // Grund: annualCurrentPace ignoriert den tatsächlichen YTD-Verlauf.
  // projectedYearEndGap wird erst nach der Rep-Performance-Sektion berechnet,
  // daher verwenden wir hier eine direkte Berechnung desselben Wertes:
  const annualTargetFromAnswers = Number(answersMap.totalRevenueTargetYearExact) > 0
    ? Number(answersMap.totalRevenueTargetYearExact) : 0;
  // Hochrechnung: YTD-Umsatz / verstrichener Jahresanteil = projizierter Jahresumsatz
  // (identisch zur projectedYearEndRevenue-Berechnung weiter unten)
  const elapsedYearRatioForGap = Math.max(0.01, Math.min(1,
    Math.max(1, Math.floor((new Date().getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000) + 1)
    / (Math.floor((new Date(new Date().getFullYear(), 11, 31).getTime() - new Date(new Date().getFullYear(), 0, 1).getTime()) / 86400000) + 1)
  ));
  const revenueAchievedYTDForGap = Number(answersMap.revenueAchievedYTDExact) >= 0
    ? Number(answersMap.revenueAchievedYTDExact) : 0;
  const projectedYearEndForGap = revenueAchievedYTDForGap > 0
    ? revenueAchievedYTDForGap / elapsedYearRatioForGap
    : currentRevenue * 12; // Fallback wenn kein YTD angegeben
  const projectedYearEndGapForRevGap = annualTargetFromAnswers > projectedYearEndForGap
    ? annualTargetFromAnswers - projectedYearEndForGap
    : 0;
  // Monatliche Forecast-Lücke: Jahresgap / verbleibende Monate (mind. 1)
  const monthsRemainingForGap = Math.max(1, Math.ceil((1 - elapsedYearRatioForGap) * 12));
  const forecastDeltaMonthly = projectedYearEndGapForRevGap > 0
    ? projectedYearEndGapForRevGap / monthsRemainingForGap
    : 0;

  // Wähle die aussagekräftigste Zahl:
  //   - Wenn Execution-Gap > 0 → nutze ihn (direkt datenbasiert)
  //   - Wenn Execution-Gap = 0 und Forecast-Delta vorhanden → nutze Forecast-Delta
  //   - Sonst 0 (kein Gap erkennbar)
  const monthlyRevenueGap = executionBasedMonthlyGap > 0
    ? executionBasedMonthlyGap
    : forecastDeltaMonthly;
  const annualRevenueGap = monthlyRevenueGap * 12;
  // Für die UI: kennzeichnen woher die Zahl stammt
  const revenueGapSource: "execution" | "forecast-delta" | "none" =
    executionBasedMonthlyGap > 0 ? "execution"
    : forecastDeltaMonthly > 0 ? "forecast-delta"
    : "none";

  // -------------------------------------------------------
  // NEW: REP PERFORMANCE V2 - PACE & FORECAST LOGIC
  // IMPORTANT: Pace calculation is based on the CURRENT CALENDAR YEAR
  // If you need a specific target year (e.g., 2026), add a targetYear field
  // -------------------------------------------------------

  // answersMap already defined above (for conversion fallback logic)

  const productiveReps =
    Number(answersMap.productiveRepsExact) > 0
      ? Number(answersMap.productiveRepsExact)
      : 1; // Fallback: 1 Rep (teamSize field removed from context)

  const totalRevenueTargetYear =
    Number(answersMap.totalRevenueTargetYearExact) > 0
      ? Number(answersMap.totalRevenueTargetYearExact)
      : 0;

  const revenueAchievedYTD =
    Number(answersMap.revenueAchievedYTDExact) >= 0
      ? Number(answersMap.revenueAchievedYTDExact)
      : 0;

  // current date helpers
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear(), 11, 31);
  const msPerDay = 1000 * 60 * 60 * 24;
  const elapsedDays = Math.max(1, Math.floor((now.getTime() - yearStart.getTime()) / msPerDay) + 1);
  const totalDaysInYear = Math.floor((yearEnd.getTime() - yearStart.getTime()) / msPerDay) + 1;
  const elapsedYearRatio = Math.max(0.01, Math.min(1, elapsedDays / totalDaysInYear));

  const expectedRevenueByNow =
    totalRevenueTargetYear > 0 ? totalRevenueTargetYear * elapsedYearRatio : 0;

  const absoluteGoalAttainmentPct =
    totalRevenueTargetYear > 0 ? (revenueAchievedYTD / totalRevenueTargetYear) * 100 : 0;

  const pacingVsPlanPct =
    expectedRevenueByNow > 0 ? (revenueAchievedYTD / expectedRevenueByNow) * 100 : 0;

  const paceGapToDate =
    expectedRevenueByNow > revenueAchievedYTD
      ? expectedRevenueByNow - revenueAchievedYTD
      : 0;

  const projectedYearEndRevenue =
    elapsedYearRatio > 0 ? revenueAchievedYTD / elapsedYearRatio : revenueAchievedYTD;

  // Forecast smoothing for early-year volatility
  const closedDealsYTD =
    Number(answersMap.closedDealsYTDExact) > 0
      ? Number(answersMap.closedDealsYTDExact)
      : 0;
  const isEarlyForecastLowConfidence =
    elapsedYearRatio < 0.20 || closedDealsYTD < 3;
  const forecastConfidenceLabel = isEarlyForecastLowConfidence
    ? "low"
    : elapsedYearRatio < 0.35 || closedDealsYTD < 6
    ? "medium"
    : "high";

  const projectedYearEndGap =
    totalRevenueTargetYear > projectedYearEndRevenue
      ? totalRevenueTargetYear - projectedYearEndRevenue
      : 0;

  const targetPerRepYear =
    productiveReps > 0 && totalRevenueTargetYear > 0
      ? totalRevenueTargetYear / productiveReps
      : 0;

  const targetPerRepMonth =
    targetPerRepYear > 0 ? targetPerRepYear / 12 : 0;

  const actualRevenuePerRepYTD =
    productiveReps > 0 ? revenueAchievedYTD / productiveReps : 0;

  const actualRevenuePerRepMonthPaced =
    elapsedYearRatio > 0
      ? actualRevenuePerRepYTD / (elapsedYearRatio * 12)
      : 0;

  // optional validation against manually entered rep target
  const manualRepTargetMonth =
    Number(answersMap.avgRevenueTargetPerRepExact) > 0
      ? Number(answersMap.avgRevenueTargetPerRepExact)
      : 0;

  const repTargetConsistencyGap =
    manualRepTargetMonth > 0 && targetPerRepMonth > 0
      ? Math.abs(manualRepTargetMonth - targetPerRepMonth)
      : 0;

  const repsOnTrackRatio = mapRepsOnTrack(answersMap.repsHitTargetShare as string);
  const pipelineCoverageRealityScore = mapPipelineCoverageReality(answersMap.pipelineCoverageReality as string);
  const pipelineConfidenceScore = mapPipelineConfidence(answersMap.pipelineConfidence as string);
  // dealMomentumLoss removed from wizard — weight redistributed to pipelineReadinessScore below
  const reviewScore = mapReviewCadence(answersMap.repReviewCadence as string);
  const targetBreakdownScore = mapTargetBreakdown(answersMap.revenueTargetBrokenDownToReps as string);

  const repPerformanceVariancePenalty =
    answersMap.repPerformanceVariance === "very-high"
      ? 35
      : answersMap.repPerformanceVariance === "high"
      ? 25
      : answersMap.repPerformanceVariance === "moderate"
      ? 12
      : answersMap.repPerformanceVariance === "low"
      ? 5
      : 15;

  const pacingHealthScore = Math.max(0, Math.min(100, pacingVsPlanPct));
  const targetManagementScore = Math.max(
    0,
    Math.min(
      100,
      (targetBreakdownScore * 0.45) +
        (reviewScore * 0.35) +
        ((repsOnTrackRatio * 100) * 0.20)
    )
  );

  const pipelineReadinessScore = Math.max(
    0,
    Math.min(
      100,
      (pipelineCoverageRealityScore * 0.55) +
        (pipelineConfidenceScore * 0.45)
    )
  );

  const teamExecutionScore = Math.max(
    0,
    Math.min(
      100,
      (pacingHealthScore * 0.45) +
        ((100 - repPerformanceVariancePenalty) * 0.30) +
        (reviewScore * 0.25)
    )
  );

  const salesRepHealthScore = Math.round(
    (targetManagementScore * 0.35) +
      (pipelineReadinessScore * 0.30) +
      (teamExecutionScore * 0.35)
  );

  // monetized potentials
  const pacingRecoveryPotentialMonthly =
    paceGapToDate > 0 ? paceGapToDate / Math.max(1, elapsedDays / 30.4) : 0;

  const repAlignmentPotentialMonthly =
    projectedYearEndGap > 0
      ? Math.min(projectedYearEndGap / 12, totalRevenueTargetYear * 0.08)
      : 0;

  const pipelineActivationPotentialMonthly =
    projectedYearEndGap > 0
      ? (projectedYearEndGap / 12) * (pipelineReadinessScore / 100) * 0.45
      : 0;

  const summarySignals = {
    hasPacingProblem: pacingVsPlanPct > 0 && pacingVsPlanPct < 85,
    hasTargetBreakdownProblem: targetBreakdownScore < 60,
    hasPipelineProblem: pipelineReadinessScore < 60,
    hasVarianceProblem: repPerformanceVariancePenalty >= 25,
    hasReviewProblem: reviewScore < 60,
  };

  // -------------------------------------------------------
  // ACTIVITY BENCHMARK — Mindestkontaktaufnahmen pro Rep
  // B2B Outbound Benchmark: ≥100/Mo = Top, ≥60/Mo = ok, <40/Mo = kritisch
  // -------------------------------------------------------
  const CONTACT_BENCHMARK_TOP = 100;   // Top-Performer
  const CONTACT_BENCHMARK_OK = 60;     // Minimum akzeptabel
  const CONTACT_BENCHMARK_CRITICAL = 40; // Kritisch unterperfomant

  const contactsPerRepPerMonth =
    productiveReps > 0 && currentContacts > 0
      ? currentContacts / productiveReps
      : 0; // 0 = unbekannt

  // Wie weit unter Benchmark?
  const contactActivityGap =
    contactsPerRepPerMonth > 0 && contactsPerRepPerMonth < CONTACT_BENCHMARK_OK
      ? CONTACT_BENCHMARK_OK - contactsPerRepPerMonth
      : 0;

  // Welche Benchmark-Stufe?
  const contactActivityLevel:
    | "unknown"
    | "critical"
    | "low"
    | "ok"
    | "top" =
    contactsPerRepPerMonth === 0
      ? "unknown"
      : contactsPerRepPerMonth < CONTACT_BENCHMARK_CRITICAL
      ? "critical"
      : contactsPerRepPerMonth < CONTACT_BENCHMARK_OK
      ? "low"
      : contactsPerRepPerMonth < CONTACT_BENCHMARK_TOP
      ? "ok"
      : "top";

  // Monatlicher Umsatzverlust durch Unteraktivität (konservativ)
  // Jeder fehlende Kontakt hätte bei aktueller CR Discovery-Calls erzeugt → Deals
  const contactActivityRevenueLoss =
    contactActivityGap > 0 && productiveReps > 0
      ? contactActivityGap *
        productiveReps *
        (convContactToDiscovery / 100) *
        (convDiscoveryToPresentation / 100) *
        (convPresentationToDeal / 100) *
        avgDealSize
      : 0;

  const contactActivitySignal = {
    contactsPerRepPerMonth: Math.round(contactsPerRepPerMonth),
    level: contactActivityLevel,
    benchmarkMin: CONTACT_BENCHMARK_OK,
    benchmarkTop: CONTACT_BENCHMARK_TOP,
    gap: Math.round(contactActivityGap),
    gapPerTeam: Math.round(contactActivityGap * productiveReps),
    monthlyRevenueLoss: Math.round(contactActivityRevenueLoss),
    annualRevenueLoss: Math.round(contactActivityRevenueLoss * 12),
    isCritical: contactActivityLevel === "critical" || contactActivityLevel === "low",
  };

  // -------------------------------------------------------
  // CAPACITY CHECK - split into current vs optimized capacity
  // -------------------------------------------------------
  const salesCycleMonths = safeNumber(funnel.salesCycleMonths, 3);
  const contactsPerRepPerMonthCapacity =
    contactsPerRepPerMonth > 0 ? contactsPerRepPerMonth : 80;
  const currentDealsPerRepPerMonthCapacity =
    contactsPerRepPerMonthCapacity *
    (currentC2D / 100) *
    (currentD2P / 100) *
    (currentP2D / 100);
  const optimizedDealsPerRepPerMonthCapacity =
    contactsPerRepPerMonthCapacity *
    (improvedC2D / 100) *
    (improvedD2P / 100) *
    (improvedP2D / 100);
  const currentMaxRevenueCapacity =
    productiveReps * currentDealsPerRepPerMonthCapacity * avgDealSize * 12;
  const optimizedMaxRevenueCapacity =
    productiveReps * optimizedDealsPerRepPerMonthCapacity * avgDealSize * 12;
  const capacityVsTargetPct =
    totalRevenueTargetYear > 0
      ? (optimizedMaxRevenueCapacity / totalRevenueTargetYear) * 100
      : 0;
  const capacityGap =
    totalRevenueTargetYear > optimizedMaxRevenueCapacity
      ? totalRevenueTargetYear - optimizedMaxRevenueCapacity
      : 0;
  const isTargetUnreachableWithCurrentSetup =
    totalRevenueTargetYear > 0 && optimizedMaxRevenueCapacity < totalRevenueTargetYear * 0.85;
  const isTargetUnreachableWithCurrentPerformance =
    totalRevenueTargetYear > 0 && currentMaxRevenueCapacity < totalRevenueTargetYear * 0.85;

  // -------------------------------------------------------
  // COST EFFICIENCY / BUDGET UTILIZATION
  // Softer and more trustworthy than "burned salary"
  // -------------------------------------------------------
  const avgRepSalaryCostMonthly =
    Number(answersMap.avgRepSalaryCostMonthly) > 0
      ? Number(answersMap.avgRepSalaryCostMonthly)
      : 4000;
  const totalSalaryCostMonthly = productiveReps * avgRepSalaryCostMonthly;
  const totalSalaryCostYear = totalSalaryCostMonthly * 12;
  const performanceRatio =
    totalRevenueTargetYear > 0
      ? Math.max(0, Math.min(1, revenueAchievedYTD / Math.max(1, totalRevenueTargetYear * elapsedYearRatio)))
      : 0;
  const inefficiencyShouldBeShown =
    totalRevenueTargetYear > 0 &&
    productiveReps >= 2 &&
    elapsedYearRatio >= 0.20 &&
    performanceRatio < 0.85;
  const inefficientSalaryCostYear = inefficiencyShouldBeShown
    ? totalSalaryCostYear * (1 - performanceRatio)
    : 0;
  const revenuePerEuroSalary =
    totalSalaryCostYear > 0 ? projectedYearEndRevenue / totalSalaryCostYear : 0;
  const targetRevenuePerEuroSalary =
    revenuePerEuroSalary >= 7 ? revenuePerEuroSalary : 6;
  const revenueEfficiencyGapAnnual =
    totalSalaryCostYear > 0
      ? Math.max(0, (targetRevenuePerEuroSalary - revenuePerEuroSalary) * totalSalaryCostYear)
      : 0;
  const salaryEfficiencyBand =
    revenuePerEuroSalary < 3
      ? "critical"
      : revenuePerEuroSalary < 5
      ? "low"
      : revenuePerEuroSalary < 7
      ? "solid"
      : "strong";

  // -------------------------------------------------------
  // GOAL PATH CALCULATION v2 - Mathematisch + audit-adjustiert
  // Team total + per rep, basierend auf audit-adjustierten Conversion-Raten
  // -------------------------------------------------------

  // Helper: clamp value to range
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  // Helper: safe null-preserving number
  const numOrNull = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  // ---- Audit-Adjustments für Goal-Path Conversion-Raten ----
  // Basieren auf den vom Audit erfassten Signalen aus answersMap

  // C2D Adjustment (Contact → Discovery)
  const adjustC2DByAudit = (): number => {
    let adj = 1.0;
    // Reaktionszeit
    const rt = answersMap.responseTime as string;
    if (rt === "under-5min" || rt === "under-1h") adj *= 1.05;
    else if (rt === "1-3days" || rt === "over-3days") adj *= 0.92;
    // Follow-up
    const fu = answersMap.followUpDiscipline as string;
    if (fu === "very-structured") adj *= 1.04;
    else if (fu === "irregular" || fu === "rarely") adj *= 0.93;
    // AvgFollowUps
    const afuRaw = numOrNull(answersMap.avgFollowUpsPerLeadExact);
    if (afuRaw !== null) {
      if (afuRaw >= 4) adj *= 1.04;
      else if (afuRaw <= 1) adj *= 0.90;
    }
    // Lead quality
    const lq = answersMap.leadQuality as string;
    if (lq === "very-high" || lq === "good") adj *= 1.03;
    else if (lq === "weak" || lq === "mixed") adj *= 0.94;
    return clamp(adj, 0.75, 1.20);
  };

  // D2P Adjustment (Discovery → Presentation/Proposal)
  const adjustD2PByAudit = (): number => {
    let adj = 1.0;
    // Wie schnell kommt ein Angebot?
    const d2pRaw = numOrNull(answersMap.daysFromDiscoveryToProposalExact);
    if (d2pRaw !== null) {
      if (d2pRaw <= 3) adj *= 1.06;
      else if (d2pRaw >= 14) adj *= 0.88;
    }
    // Next Step Disziplin nach Proposal
    const nsp = numOrNull(answersMap.proposalsWithNextStepPercentExact);
    if (nsp !== null) {
      if (nsp >= 80) adj *= 1.06;
      else if (nsp <= 40) adj *= 0.88;
    }
    // Decision Maker im Erstgespräch
    const dmfm = numOrNull(answersMap.decisionMakerInFirstMeetingPercentExact);
    if (dmfm !== null) {
      if (dmfm >= 60) adj *= 1.05;
      else if (dmfm <= 25) adj *= 0.90;
    }
    // No-show Rate Discovery
    const noshow = numOrNull(answersMap.discoveryNoShowRatePercentExact);
    if (noshow !== null) {
      if (noshow >= 25) adj *= 0.88;
      else if (noshow <= 5) adj *= 1.03;
    }
    // Discovery Framework
    const df3 = answersMap.discoveryFrameworkStep3 as string;
    if (df3 === "yes") adj *= 1.04;
    else if (df3 === "no") adj *= 0.92;
    return clamp(adj, 0.70, 1.25);
  };

  // P2D Adjustment (Presentation → Deal)
  const adjustP2DByAudit = (): number => {
    let adj = 1.0;
    // Decision Maker involvement
    const dmi = answersMap.decisionMakerInProcess as string;
    if (dmi === "always") adj *= 1.07;
    else if (dmi === "rarely" || dmi === "never") adj *= 0.88;
    // Pipeline Hygiene
    const stale = numOrNull(answersMap.stalePipelineSharePercentExact);
    if (stale !== null) {
      if (stale >= 40) adj *= 0.88;
      else if (stale <= 10) adj *= 1.04;
    }
    // Forecast Coverage
    const fcr = numOrNull(answersMap.forecastCoverageRatioExact);
    if (fcr !== null) {
      if (fcr >= 3.0) adj *= 1.05;
      else if (fcr < 2.0) adj *= 0.91;
    }
    // Next step coverage (replaces the removed nextStepDiscipline question)
    const nsc = Number(answersMap.nextStepCoveragePercent);
    if (Number.isFinite(nsc)) {
      if (nsc >= 80) adj *= 1.04;
      else if (nsc < 40) adj *= 0.90;
    }
    return clamp(adj, 0.70, 1.25);
  };

  // Berechne Confidence Score für Goal Path (0–100)
  const buildGoalPathConfidence = (): { score: number; label: string; factors: string[] } => {
    let score = 60; // Basis
    const factors: string[] = [];

    // ── Datenvollständigkeit (Volumen) ──────────────────────────────
    if (currentContacts > 0 && currentDiscovery > 0 && currentPresentations > 0 && currentDeals > 0) {
      score += 15;
    } else {
      score -= 15;
      factors.push("Nicht alle Funnel-Stufen belastbar befüllt");
    }

    // ── Datenpräzision: Conversion-Quellen ──────────────────────────
    // Volumen-basiert (höchste Qualität)
    const c2dFromVol = rawContacts != null && rawContacts > 0 && rawDiscovery != null;
    const d2pFromVol = rawDiscovery != null && rawDiscovery > 0 && rawPresentations != null;
    const p2dFromVol = rawPresentations != null && rawPresentations > 0 && rawDeals != null;

    // Exakt direkt eingegeben (mittlere Qualität)
    const c2dExactDirect = !c2dFromVol && rawContacts == null
      && answersMap.convLeadToDiscoveryExact != null
      && !answersMap.convLeadToDiscoveryUnknown;
    const d2pExactDirect = !d2pFromVol && rawDiscovery == null
      && answersMap.convDiscoveryToOfferExact != null
      && !answersMap.convDiscoveryToOfferUnknown;
    const p2dExactDirect = !p2dFromVol && rawPresentations == null
      && answersMap.convOfferToCloseExact != null
      && !answersMap.convOfferToCloseUnknown;

    // Range statt exakter Zahl (niedrigere Qualität)
    const c2dFromRange = !c2dFromVol && !c2dExactDirect && answersMap.convLeadToDiscoveryExact != null;
    const d2pFromRange = !d2pFromVol && !d2pExactDirect && answersMap.convDiscoveryToOfferExact != null;
    const p2dFromRange = !p2dFromVol && !p2dExactDirect && answersMap.convOfferToCloseExact != null;

    // Bonus für exakte Direkteingaben (Nutzer kennt seinen Funnel)
    const exactDirectCount = [c2dExactDirect, d2pExactDirect, p2dExactDirect].filter(Boolean).length;
    if (exactDirectCount > 0) {
      score += exactDirectCount * 3; // +3 pro exakt eingegebener Rate (max +9)
      // kein Factor-Push – das ist positiv
    }

    // Kleiner Abzug für Range-Angaben (Schätzung, nicht Wissen)
    const rangeCount = [c2dFromRange, d2pFromRange, p2dFromRange].filter(Boolean).length;
    if (rangeCount > 0) {
      score -= rangeCount * 2; // -2 pro Range-Angabe (max -6)
      factors.push(`${rangeCount} Conversion-Rate(n) nur als Bereich angegeben`);
    }

    // Abzug wenn Conversion-Stufen komplett unbekannt
    const unknownCount = [
      answersMap.convLeadToDiscoveryUnknown,
      answersMap.convDiscoveryToOfferUnknown,
      answersMap.convOfferToCloseUnknown,
    ].filter(Boolean).length;
    if (unknownCount > 0 && currentContacts === 0) {
      score -= unknownCount * 3; // -3 pro unbekannter Rate (max -9)
      factors.push(`${unknownCount} Conversion-Rate(n) nicht bekannt`);
    }

    // ── Umsatzziel & YTD ────────────────────────────────────────────
    if (totalRevenueTargetYear > 0 && revenueAchievedYTD > 0) {
      score += 10;
    } else {
      score -= 10;
      factors.push("Umsatzziel oder YTD-Wert fehlt");
    }

    // ── Forecasting ─────────────────────────────────────────────────
    if (isEarlyForecastLowConfidence) {
      score -= 10;
      factors.push("Wenig Jahresverlauf (Prognose unscharf)");
    }

    // ── Datenqualität weitere Signale ───────────────────────────────
    if ((answersMap.stalePipelineSharePercentExact as number) == null) {
      score -= 5;
      factors.push("Stale Pipeline unbekannt");
    }
    if ((answersMap.daysFromDiscoveryToProposalExact as number) == null) {
      score -= 5;
      factors.push("Discovery→Proposal-Zeit unbekannt");
    }

    score = clamp(score, 10, 95);
    const label = score >= 75 ? "hoch" : score >= 50 ? "mittel" : "niedrig";
    return { score: Math.round(score), label, factors };
  };

  const goalPathConfidence = buildGoalPathConfidence();

  // ---- Audit-adjustierte Conversion-Raten für Soll-Berechnung ----
  const c2dAdjFactor = adjustC2DByAudit();
  const d2pAdjFactor = adjustD2PByAudit();
  const p2dAdjFactor = adjustP2DByAudit();

  // Basis: aktuelle Raten, falls vorhanden; sonst Benchmark-Fallback
  const baseC2DForGoal = currentC2D > 0 ? currentC2D : benchmarkContactToDiscovery;
  const baseD2PForGoal = currentD2P > 0 ? currentD2P : benchmarkDiscoveryToPresentation;
  const baseP2DForGoal = currentP2D > 0 ? currentP2D : benchmarkPresentationToDeal;

  // Realistisch adjustierte Raten für Goal-Path-Szenarien
  // C2D: max 15 % (auch bei perfekter Ausführung selten höher)
  // D2P: max 75 % (sehr gut qualifizierter Funnel)
  // P2D: max 55 % (sehr gut geführtes Closing)
  const adjustedC2D = clamp(baseC2DForGoal * c2dAdjFactor, 1, 15);
  const adjustedD2P = clamp(baseD2PForGoal * d2pAdjFactor, 1, 75);
  const adjustedP2D = clamp(baseP2DForGoal * p2dAdjFactor, 1, 55);

  const goalPathUsesFallbackBenchmarks =
    currentC2D === 0 || currentD2P === 0 || currentP2D === 0;

  // Basis-Variablen
  const remainingRevenueNeeded =
    totalRevenueTargetYear > revenueAchievedYTD
      ? totalRevenueTargetYear - revenueAchievedYTD
      : 0;
  const monthsRemainingApprox = Math.max(1, Math.ceil((totalDaysInYear - elapsedDays) / 30.4));
  const remainingMonthsFloat = Math.max(1, (totalDaysInYear - elapsedDays) / 30.4);
  const requiredMonthlyRevenueFromNow =
    remainingRevenueNeeded > 0
      ? remainingRevenueNeeded / monthsRemainingApprox
      : 0;
  const avgDealSizeForGoalPath = avgDealSize > 0 ? avgDealSize : 1;
  const requiredDealsRemaining =
    remainingRevenueNeeded > 0
      ? remainingRevenueNeeded / avgDealSizeForGoalPath
      : 0;
  const requiredDealsPerMonthFromNow =
    requiredDealsRemaining > 0
      ? requiredDealsRemaining / monthsRemainingApprox
      : 0;

  // Aktuelle Funnel-Raten als Ratios (NO fallback)
  const currentContactToDiscoveryRatio =
    currentContacts > 0
      ? currentDiscovery / currentContacts
      : 0;
  const currentDiscoveryToPresentationRatio =
    currentDiscovery > 0
      ? currentPresentations / currentDiscovery
      : 0;
  const currentPresentationToDealRatio =
    currentPresentations > 0
      ? currentDeals / currentPresentations
      : 0;

  // -----------------------------------------------------------------------
  // A) MATHEMATISCHER PFAD — aktuelle (nicht-adjustierte) Conversion-Raten
  //    Basis: currentC2D/D2P/P2D, Fallback auf Benchmarks wenn 0
  // -----------------------------------------------------------------------
  const mathC2DRatio = (currentC2D > 0 ? currentC2D : benchmarkContactToDiscovery) / 100;
  const mathD2PRatio = (currentD2P > 0 ? currentD2P : benchmarkDiscoveryToPresentation) / 100;
  const mathP2DRatio = (currentP2D > 0 ? currentP2D : benchmarkPresentationToDeal) / 100;

  // Mathematische Rückrechnung (required... = rechnerischer Basispfad)
  const requiredPresentationsRemaining =
    mathP2DRatio > 0 ? requiredDealsRemaining / mathP2DRatio : 0;
  const requiredPresentationsPerMonthFromNow =
    requiredPresentationsRemaining > 0 ? requiredPresentationsRemaining / monthsRemainingApprox : 0;
  const requiredDiscoveryCallsRemaining =
    mathD2PRatio > 0 ? requiredPresentationsRemaining / mathD2PRatio : 0;
  const requiredDiscoveryCallsPerMonthFromNow =
    requiredDiscoveryCallsRemaining > 0 ? requiredDiscoveryCallsRemaining / monthsRemainingApprox : 0;
  const requiredContactsRemaining =
    mathC2DRatio > 0 ? requiredDiscoveryCallsRemaining / mathC2DRatio : 0;
  const requiredContactsPerMonthFromNow =
    requiredContactsRemaining > 0 ? requiredContactsRemaining / monthsRemainingApprox : 0;

  // Per-rep mathematisch
  const activeRepsForGoalPath = Math.max(1, productiveReps);
  const requiredDealsPerRepPerMonthFromNow = requiredDealsPerMonthFromNow / activeRepsForGoalPath;
  const requiredPresentationsPerRepPerMonthFromNow = requiredPresentationsPerMonthFromNow / activeRepsForGoalPath;
  const requiredDiscoveryCallsPerRepPerMonthFromNow = requiredDiscoveryCallsPerMonthFromNow / activeRepsForGoalPath;
  const requiredContactsPerRepPerMonthFromNow = requiredContactsPerMonthFromNow / activeRepsForGoalPath;

  // -----------------------------------------------------------------------
  // B) REALISTISCHER PFAD — audit-adjustierte Conversion-Raten
  //    Direkt aus adjustedC2D/D2P/P2D — KEIN zusätzlicher Friction-Faktor
  // -----------------------------------------------------------------------
  const adjustedC2DRatio = adjustedC2D / 100;
  const adjustedD2PRatio = adjustedD2P / 100;
  const adjustedP2DRatio = adjustedP2D / 100;

  const realisticRequiredPresentationsRemaining =
    adjustedP2DRatio > 0 ? requiredDealsRemaining / adjustedP2DRatio : 0;
  const realisticRequiredPresentationsPerMonthFromNow =
    realisticRequiredPresentationsRemaining > 0 ? realisticRequiredPresentationsRemaining / monthsRemainingApprox : 0;
  const realisticRequiredDiscoveryCallsRemaining =
    adjustedD2PRatio > 0 ? realisticRequiredPresentationsRemaining / adjustedD2PRatio : 0;
  const realisticRequiredDiscoveryCallsPerMonthFromNow =
    realisticRequiredDiscoveryCallsRemaining > 0 ? realisticRequiredDiscoveryCallsRemaining / monthsRemainingApprox : 0;
  const realisticRequiredContactsRemaining =
    adjustedC2DRatio > 0 ? realisticRequiredDiscoveryCallsRemaining / adjustedC2DRatio : 0;
  const realisticRequiredContactsPerMonthFromNow =
    realisticRequiredContactsRemaining > 0 ? realisticRequiredContactsRemaining / monthsRemainingApprox : 0;

  // Per-rep realistisch
  const realisticRequiredContactsPerRepPerMonthFromNow = realisticRequiredContactsPerMonthFromNow / activeRepsForGoalPath;
  const realisticRequiredDiscoveryCallsPerRepPerMonthFromNow = realisticRequiredDiscoveryCallsPerMonthFromNow / activeRepsForGoalPath;
  const realisticRequiredPresentationsPerRepPerMonthFromNow = realisticRequiredPresentationsPerMonthFromNow / activeRepsForGoalPath;

  // IST per rep
  const currentContactsPerRepPerMonth = activeRepsForGoalPath > 0 ? currentContacts / activeRepsForGoalPath : 0;
  const currentDiscoveryCallsPerRepPerMonth = activeRepsForGoalPath > 0 ? currentDiscovery / activeRepsForGoalPath : 0;
  const currentPresentationsPerRepPerMonth = activeRepsForGoalPath > 0 ? currentPresentations / activeRepsForGoalPath : 0;
  const currentDealsPerRepPerMonth = activeRepsForGoalPath > 0 ? currentDeals / activeRepsForGoalPath : 0;

  // Erforderliche Conversion bei aktuellem Volumen (für Diagnose-Block)
  const expectedContactsRemainingAtCurrentVolume = currentContacts * remainingMonthsFloat;
  const expectedDiscoveriesRemainingAtCurrentVolume = currentDiscovery * remainingMonthsFloat;
  const expectedPresentationsRemainingAtCurrentVolume = currentPresentations * remainingMonthsFloat;
  const requiredPresentationToDealPctAtCurrentVolume =
    expectedPresentationsRemainingAtCurrentVolume > 0
      ? Math.min(100, (requiredDealsRemaining / expectedPresentationsRemainingAtCurrentVolume) * 100)
      : null;
  const requiredDiscoveryToPresentationPctAtCurrentVolume =
    expectedDiscoveriesRemainingAtCurrentVolume > 0 && requiredPresentationsRemaining > 0
      ? Math.min(100, (requiredPresentationsRemaining / expectedDiscoveriesRemainingAtCurrentVolume) * 100)
      : null;
  const requiredContactToDiscoveryPctAtCurrentVolume =
    expectedContactsRemainingAtCurrentVolume > 0 && requiredDiscoveryCallsRemaining > 0
      ? Math.min(100, (requiredDiscoveryCallsRemaining / expectedContactsRemainingAtCurrentVolume) * 100)
      : null;

  // Sales Cycle Warning - computed after goalPath vars are available
  const isSalesCycleTooLong =
    salesCycleMonths > 0 && monthsRemainingApprox > 0
      ? salesCycleMonths >= monthsRemainingApprox * 0.75
      : false;
  const dealsUnreachableDueToSalesCycle =
    isSalesCycleTooLong && requiredDealsRemaining > 0
      ? Math.ceil(
          requiredDealsRemaining *
            (salesCycleMonths / Math.max(salesCycleMonths, monthsRemainingApprox))
        )
      : 0;

  // NEW: Diagnostic Scoring System (MUST come before topLevers)
  // answersMap already declared above for rep performance calculations
  const diagnosticScoring = calculateDiagnosticScoring(answersMap);
  const priorityTitles = mapWeakestDimensionsToPriorityTitles(
    diagnosticScoring.weakestDimensionsRanked
  );

  // Build top levers - Dynamic based on diagnostic scoring
  const topLevers = priorityTitles.map((title, index) => {
    const impactShare = index === 0 ? 0.38 : index === 1 ? 0.32 : 0.22;
    const dim = diagnosticScoring.weakestDimensionsRanked[index];
    const whyItMatters =
      dim === "WEBSITE"
        ? "Der Inbound-Prozess verliert zu früh qualifizierte Kontakte — zu langsame Reaktionszeiten und fehlende Qualifizierungsstandards kosten täglich Umsatz."
        : dim === "DEMAND"
        ? "Das Problem beginnt vor dem Gespräch: Qualität, Passung oder Erwartung der Leads sind nicht sauber genug."
        : dim === "CONVERSION"
        ? "Im Funnel entstehen messbare Verluste zwischen Kontakt, Gespräch und reifer Opportunity."
        : dim === "SALES_QUALITY"
        ? "Discovery, Bedarfstiefe und Kaufreife werden nicht konsequent genug herausgearbeitet."
        : dim === "PROCESS"
        ? "Fehlende Ownership, Next Steps und Steuerung machen die Performance instabil."
        : "Schwache Differenzierung und zu wenig Proof reduzieren Vertrauen, Kaufbereitschaft und Abschlusswahrscheinlichkeit.";
    return {
      title,
      monthlyImpact: Math.round(monthlyRevenueGap * impactShare),
      annualImpact: Math.round(annualRevenueGap * impactShare),
      whyItMatters,
      dimension: dim, // PRIO 2 FIX: Include dimension for causal mapping
    };
  });
  
  // NEW: Augment topLevers with Rep Performance levers
  const augmentedTopLevers = [...topLevers];

  if (summarySignals.hasPacingProblem) {
    augmentedTopLevers.push({
      title: "Sales Pace auf Zielkurs bringen",
      monthlyImpact: Math.round(pacingRecoveryPotentialMonthly),
      annualImpact: Math.round(pacingRecoveryPotentialMonthly * 12),
      whyItMatters:
        "Der bisher erzielte Umsatz liegt unter dem linearen Sollverlauf. Ohne Gegensteuerung wird das Jahresziel voraussichtlich verfehlt.",
      dimension: "REP_PERFORMANCE",
    });
  }

  if (summarySignals.hasTargetBreakdownProblem) {
    augmentedTopLevers.push({
      title: "Umsatzziele operativ herunterbrechen",
      monthlyImpact: Math.round(repAlignmentPotentialMonthly),
      annualImpact: Math.round(repAlignmentPotentialMonthly * 12),
      whyItMatters:
        "Das Umsatzziel ist nicht konsequent auf Reps oder Teams heruntergebrochen. Das erschwert Steuerung, Review und saubere Verantwortlichkeit.",
      dimension: "REP_PERFORMANCE",
    });
  }

  if (summarySignals.hasPipelineProblem) {
    augmentedTopLevers.push({
      title: "Pipeline pro Rep belastbar machen",
      monthlyImpact: Math.round(pipelineActivationPotentialMonthly),
      annualImpact: Math.round(pipelineActivationPotentialMonthly * 12),
      whyItMatters:
        "Ein Teil der Reps verfügt nicht über ausreichend belastbare Pipeline. Dadurch entsteht ein strukturelles Risiko für die Zielerreichung.",
      dimension: "REP_PERFORMANCE",
    });
  }

  augmentedTopLevers.sort((a, b) => (b.monthlyImpact || 0) - (a.monthlyImpact || 0));
  
  // NEW: Rep Quick Wins
  const unknownFields = buildUnknownFields(answersMap, input);

  // PRIO 4 FIX: Calculate completeness based on REAL user questions only
  // (excludes *Exact, *Unknown, and other helper fields)
  const completenessScore = calculateCompletenessScore(input.answers, unknownFields);
  
  // Extract stats for questionsUsed
  const realQuestions = input.answers.filter(a => {
    const id = a.questionId;
    return !id.endsWith("Exact") && !id.endsWith("Unknown") && !id.startsWith("_");
  });
  const answeredQuestions = realQuestions.filter(
    a => a.value !== null && a.value !== undefined && a.value !== "not-known"
  ).length;
  const totalQuestions = realQuestions.length;

  // Build dataGaps array for UI with specific impacts and recommendations
  const dataGapDetails: Record<string, { impact: string; recommendation: string; severity: "critical" | "warning" }> = {
    "Conversion Lead → Discovery": {
      impact: "Ohne diese Rate können Sie nicht berechnen, wie viele Kontakte nötig sind, um Ihre Discovery-Call-Zahl zu erreichen. Die Aktivitätsplanung Ihrer Reps basiert auf Annahmen.",
      recommendation: "Tracken Sie im CRM, welcher Anteil der Kontakte zu einem qualifizierten Erstgespräch führt. Segmentieren Sie nach Kanal (E-Mail, LinkedIn, Telefon) um die effizienteste Quelle zu identifizieren.",
      severity: "critical" as const,
    },
    "Conversion Discovery → Präsentation": {
      impact: "Sie wissen nicht, wie effektiv Ihre Discovery Calls qualifizieren. Ohne diese Rate präsentieren Sie möglicherweise zu früh an nicht-qualifizierte Leads oder verlieren qualifizierte Kontakte.",
      recommendation: "Definieren Sie klare Qualifizierungskriterien (Budget, Bedarf, Entscheider). Erfassen Sie nach jedem Discovery Call, ob er zu einer Präsentation geführt hat und dokumentieren Sie die Gründe bei 'Nein'.",
      severity: "critical" as const,
    },
    "Conversion Angebot → Abschluss": {
      impact: "Ihre Win-Rate ist unbekannt — das ist der kritischste blinde Fleck im gesamten Funnel. Ohne diese Zahl können Sie nicht beurteilen, ob Ihre Angebote kompetitiv sind oder ob Ihr Closing-Prozess Schwächen hat.",
      recommendation: "Tracken Sie jeden Abschluss und jeden verlorenen Deal mit Verlustgrund. Analysieren Sie Win-Rate nach Deal-Größe, Branche und Rep. Starten Sie mit den letzten 20 Deals als Baseline.",
      severity: "critical" as const,
    },
    "Qualifizierte Kontaktaufnahmen": {
      impact: "Ohne diese Zahl können Sie nicht berechnen, wie viele Leads Sie brauchen, um Ihre Umsatzziele zu erreichen. Ihre Pipeline-Planung basiert auf Bauchgefühl statt auf Fakten.",
      recommendation: "Erfassen Sie ab sofort alle qualifizierten Kontaktaufnahmen im CRM. Definieren Sie klare Kriterien, wann ein Kontakt als 'qualifiziert' gilt (z.B. Budget, Bedarf, Entscheidungsbefugnis).",
      severity: "critical" as const,
    },
    "Erstgespräche / Discovery Calls": {
      impact: "Sie wissen nicht, wie viele Ihrer Kontakte tatsächlich in echte Verkaufsgespräche münden. Das macht es unmöglich, Engpässe im Funnel zu identifizieren oder die Qualität Ihrer Leads zu bewerten.",
      recommendation: "Tracken Sie jeden Discovery Call im CRM mit Datum und Ergebnis. Nutzen Sie Kalender-Integrationen, um automatisch zu erfassen, wann ein Erstgespräch stattgefunden hat.",
      severity: "critical" as const,
    },
    "Präsentationen / Demos": {
      impact: "Ohne diese Kennzahl sehen Sie nicht, ob Ihre Discovery Calls erfolgreich in Präsentationen konvertieren. Sie können nicht messen, ob Ihre Qualifizierung funktioniert oder ob Sie zu früh präsentieren.",
      recommendation: "Erfassen Sie jede Präsentation/Demo als eigene Pipeline-Stage. Dokumentieren Sie, welche Features gezeigt wurden und wie der Kunde reagiert hat.",
      severity: "critical" as const,
    },
    "Abschlüsse pro Monat": {
      impact: "Sie können Ihren Umsatz nicht planen und wissen nicht, ob Sie auf Kurs sind. Forecast, Ressourcenplanung und Wachstumsziele basieren auf Schätzungen statt auf echten Zahlen.",
      recommendation: "Erfassen Sie jeden Abschluss mit Datum, Deal-Größe und Produkt im CRM. Erstellen Sie ein monatliches Dashboard, das Ihnen die Anzahl der Abschlüsse in Echtzeit zeigt.",
      severity: "critical" as const,
    },
    "Durchschnittliche Deal-Größe": {
      impact: "Sie wissen nicht, wie viel Umsatz ein typischer Deal bringt. Das macht Revenue-Forecasts ungenau und Sie können nicht erkennen, ob Sie zu viele kleine oder zu wenige große Deals machen.",
      recommendation: "Berechnen Sie die durchschnittliche Deal-Größe der letzten 12 Monate. Segmentieren Sie nach Produkten/Services und tracken Sie die Entwicklung monatlich.",
      severity: "critical" as const,
    },
    "Sales Cycle": {
      impact: "Ohne Sales Cycle-Daten können Sie nicht planen, wann aus heutigen Leads Umsatz wird. Ihre Pipeline-Prognosen sind unzuverlässig und Sie erkennen nicht, wenn Deals zu lange hängen bleiben.",
      recommendation: "Messen Sie die Zeit vom ersten Kontakt bis zum Abschluss für jeden Deal. Berechnen Sie den Median (nicht Durchschnitt!) der letzten 20 Deals als Ihre Baseline.",
      severity: "critical" as const,
    },
    "Reaktionszeit": {
      impact: "Langsame Reaktionszeiten töten Deals, bevor sie entstehen. Studien zeigen: Nach 5 Minuten sinkt die Chance auf ein Gespräch um 400%. Sie verlieren qualifizierte Leads an schnellere Wettbewerber.",
      recommendation: "Implementieren Sie Lead-Routing mit automatischen Benachrichtigungen. Setzen Sie ein SLA: Jeder Lead wird innerhalb von 60 Minuten kontaktiert. Messen Sie die tatsächliche Reaktionszeit im CRM.",
      severity: "critical" as const,
    },
    "Follow-up-Disziplin": {
      impact: "Ohne systematisches Follow-up versanden 80% Ihrer Opportunities. Sie verlassen sich auf das Gedächtnis Ihrer Vertriebler statt auf Prozesse. Deals fallen durch die Ritzen.",
      recommendation: "Definieren Sie Follow-up-Regeln: Nach jedem Gespräch wird der nächste Termin vereinbart. Nutzen Sie CRM-Automatisierung für Erinnerungen. Messen Sie, wie viele Deals ohne Follow-up verloren gehen.",
      severity: "warning" as const,
    },
    "Pipeline-Transparenz": {
      impact: "Ihr Management fliegt blind. Sie können nicht sehen, wo Deals stecken bleiben, welche Vertriebler Hilfe brauchen oder ob Ihre Pipeline gesund ist. Forecasts sind Ratespiele.",
      recommendation: "Implementieren Sie klare Pipeline-Stages mit Definitions-Checklisten. Jeder Deal muss eine Stage haben. Erstellen Sie ein wöchentliches Pipeline-Review mit dem Team.",
      severity: "critical" as const,
    },
    "Lead-Qualität": {
      impact: "Sie verschwenden Zeit mit unqualifizierten Leads. Ihr Team arbeitet an Opportunities, die nie schließen werden. Sie können nicht unterscheiden zwischen echten Interessenten und Zeitverschwendern.",
      recommendation: "Führen Sie ein Lead-Scoring-System ein (z.B. BANT: Budget, Authority, Need, Timeline). Qualifizieren Sie jeden Lead bevor er in den Sales-Prozess geht. Messen Sie die Conversion-Rate nach Lead-Quelle.",
      severity: "warning" as const,
    },
    "Teamgröße": {
      impact: "Ohne zu wissen, wie viele Leute im Vertrieb arbeiten, können Sie keine Produktivitätskennzahlen berechnen. Sie wissen nicht, ob Sie unterbesetzt sind oder ob Ihr Team ineffizient arbeitet.",
      recommendation: "Dokumentieren Sie Ihre aktuelle Teamgröße (FTE = Full Time Equivalents). Berechnen Sie Revenue per Rep und Deals per Rep als Benchmark für zukünftiges Wachstum.",
      severity: "warning" as const,
    },
  };

  // Build weakest dimension cards from diagnostic scoring
  const weakestDimensionCards = diagnosticScoring.priorityDrivers.map((driver, index) => ({
    id: `dimension-${driver.dimension.toLowerCase()}`,
    field: driver.dimension,
    title: driver.title,
    severity: (index === 0 ? "critical" : "warning") as "critical" | "warning",
    impact: driver.summary,
    recommendation:
      driver.dimension === "WEBSITE"
        ? "Definieren Sie klare Inbound-SLAs, Qualifizierungsstandards und einen strukturierten Kontakt-zu-Discovery-Prozess."
        : driver.dimension === "DEMAND"
        ? "Prüfen Sie Leadquellen, Passung, ICP und Vorqualifizierung."
        : driver.dimension === "CONVERSION"
        ? "Analysieren Sie die Brüche zwischen Kontakt, Gespräch, Opportunity und Angebot."
        : driver.dimension === "SALES_QUALITY"
        ? "Standardisieren Sie Discovery und Qualifizierung deutlich stärker."
        : driver.dimension === "PROCESS"
        ? "Verankern Sie Ownership, Follow-up-Standards und Pipeline-Reviews."
        : "Schärfen Sie Positionierung, Nutzenversprechen und Differenzierung.",
    estimatedRevenueLoss: Math.round(monthlyRevenueGap * (index === 0 ? 0.3 : 0.18)),
  }));
  
  const existingDataGaps = unknownFields.map((field, index) => {
    const details = dataGapDetails[field] || {
      impact: "Ohne diese Kennzahl können Sie nicht präzise steuern.",
      recommendation: "Bitte erfassen Sie diese Kennzahl systematisch.",
      severity: "warning" as const,
    };
    
    return {
      id: `gap_${index}`,
      field,
      title: `${field} nicht bekannt`,
      severity: details.severity,
      impact: details.impact,
      recommendation: details.recommendation,
      estimatedRevenueLoss: 0,
    };
  });
  
  const dataGaps = [
    ...weakestDimensionCards,
    ...existingDataGaps,
  ];

  // Build structural causes ("Why" layer for UI)
  const structuralCauses = buildStructuralCauses({
    diagnosticScoring,
    monthlyRevenueGap,
    annualRevenueGap,
    topLevers: augmentedTopLevers.slice(0, 6) as Array<{ title: string; monthlyImpact: number; annualImpact: number; whyItMatters: string; dimension?: DiagnosticDimension; }>,
    convPresentationToDeal,
    benchmarkPresentationToDeal,
    reverseFunnel: {
      gapContacts: Math.max(0, realisticRequiredContactsPerMonthFromNow - currentContacts),
      istContactsPerRepPerMonth: productiveReps > 0 ? currentContacts / productiveReps : 0,
      targetContactsPerRepPerMonth: productiveReps > 0 ? realisticRequiredContactsPerMonthFromNow / productiveReps : 0,
    },
    salesCycleWarning: { salesCycleMonths, isSalesCycleTooLong },
    rawAnswers: answersMap,
    teamPerformance: productiveReps > 0 ? {
      productiveReps,
      repHealthScore: salesRepHealthScore,
      projectedYearEndGap,
      actualRevenuePerRepMonthPaced,
      targetPerRepMonth,
    } : null,
  });

  // ── Rep-Execution Raten: aktuelle User-Raten, geclampt auf realistische Maxima ──
  // Priorität 1: echte User-Rate aus Volumen oder Direkteingabe
  // Priorität 2: Benchmark-Fallback wenn keine Rate vorhanden
  // Clamp-Maxima: C2D max 20%, D2P max 70%, P2D max 55%
  // → Verhindert 100%-Werte wenn z.B. Discovery = Presentations
  const repP2DRatio = clamp(
    currentP2D > 0 ? currentP2D : benchmarkPresentationToDeal,
    1, 55
  ) / 100;
  const repD2PRatio = clamp(
    currentD2P > 0 ? currentD2P : benchmarkDiscoveryToPresentation,
    1, 70
  ) / 100;
  const repC2DRatio = clamp(
    currentC2D > 0 ? currentC2D : benchmarkContactToDiscovery,
    1, 20
  ) / 100;

  const repExecutionPath = buildRepExecutionPath({
    productiveReps,
    avgDealSize,
    currentMonthlyDeals: currentDeals,
    currentMonthlyPresentations: currentPresentations,
    currentMonthlyDiscoveryCalls: currentDiscovery,
    currentMonthlyContacts: currentContacts,
    currentMonthlyRevenue: currentRevenue,
    requiredMonthlyRevenueFromNow,
    annualRevenueTarget: totalRevenueTargetYear,   // Jahresziel als Fallback
    adjustedP2DRatio: repP2DRatio,
    adjustedD2PRatio: repD2PRatio,
    adjustedC2DRatio: repC2DRatio,
    confidenceScore: goalPathConfidence.score,
    usesFallbackBenchmarks: goalPathUsesFallbackBenchmarks,
  });

  const focusPriorities = buildFocusPriorities({
    topLevers: augmentedTopLevers.slice(0, 6) as Array<{ title: string; monthlyImpact: number; annualImpact: number; whyItMatters: string; dimension?: DiagnosticDimension; }>,
    diagnosticScoring,
  });

  return {
    version: "1.0.0",
    confidence: {
      overall: completenessScore,
    },
    questionsUsed: {
      core: totalQuestions,
      context: 0,
      action: 0,
      answered: answeredQuestions,
    },
    categories: [],
    diagnostic: {
      categoryNeeds: {},
      operationalState: {},
      driverGaps: [],
    },
    simulation: {
      current: {
        contacts: currentContacts,
        discoveryCalls: currentDiscovery,
        presentations: currentPresentations,
        deals: currentDeals,
        revenue: currentRevenue,
        salesCycleMonths: safeNumber(funnel.salesCycleMonths, 3),
      },
      scenarios: {
        conservative: {
          current: { 
            revenue: currentRevenue, 
            deals: currentDeals,
            discoveryCalls: currentDiscovery,
            presentations: currentPresentations,
            contacts: currentContacts,
          },
          potential: { 
            revenue: conservativeRevenue, 
            deals: conservativeDeals,
            discoveryCalls: conservativeDiscovery,
            presentations: conservativePresentations,
            contacts: currentContacts,
          },
          additionalRevenuePerMonth: conservativeRevenue - currentRevenue,
        },
        realistic: {
          current: { 
            revenue: currentRevenue, 
            deals: currentDeals,
            discoveryCalls: currentDiscovery,
            presentations: currentPresentations,
            contacts: currentContacts,
          },
          potential: { 
            revenue: realisticRevenue, 
            deals: realisticDeals,
            discoveryCalls: realisticDiscovery,
            presentations: realisticPresentations,
            contacts: currentContacts,
          },
          additionalRevenuePerMonth: monthlyRevenueGap,
        },
        ambitious: {
          current: { 
            revenue: currentRevenue, 
            deals: currentDeals,
            discoveryCalls: currentDiscovery,
            presentations: currentPresentations,
            contacts: currentContacts,
          },
          potential: { 
            revenue: ambitiousRevenue, 
            deals: ambitiousDeals,
            discoveryCalls: ambitiousDiscovery,
            presentations: ambitiousPresentations,
            contacts: currentContacts,
          },
          additionalRevenuePerMonth: ambitiousRevenue - currentRevenue,
        },
      },
    },
    benchmarks: null,
    levers: augmentedTopLevers.slice(0, 5),
    risks: [],
    recommendations: [],
    presentation: {},
    notes: [],
    
    // NEW: Add data that ResultsMaster needs
    companyName: companyContext.companyName || "Ihr Unternehmen",
    dataGaps,
    topLevers: augmentedTopLevers.slice(0, 3),
    completenessScore,
    acquisitionMethods: companyContext.acquisitionMethods || {},
    diagnosticScoring,
    priorityTitles,
    structuralCauses,
    repExecutionPath,
    focusPriorities,

    // NEW: Add normalized/computed data for UI
    normalizedData: {
      companyName: companyContext.companyName || "Ihr Unternehmen",
      industry: companyContext.industry || "B2B",
      monthlyContacts: currentContacts,
      monthlyDiscoveryCalls: currentDiscovery,
      monthlyPresentations: currentPresentations,
      monthlyDeals: currentDeals,
      averageDealSize: avgDealSize,
      monthlyRevenue: currentRevenue,
      salesCycleMonths: safeNumber(funnel.salesCycleMonths, 3),
      responseTime: funnel.responseTime || "not-known",
      followUpDiscipline: funnel.followUpDiscipline || "not-known",
      pipelineTransparency: funnel.pipelineTransparency || "not-known",
      leadQuality: funnel.leadQuality || "not-known",
      teamSize: productiveReps, // Use productiveReps from Sales Rep section
    },
    
    computedMetrics: {
      // Current conversions
      convContactToDiscovery,
      convDiscoveryToPresentation,
      convPresentationToDeal,
      
      // Benchmark conversions (true market benchmarks)
      benchmarkContactToDiscovery,
      benchmarkDiscoveryToPresentation,
      benchmarkPresentationToDeal,
      // Optimized (realistic improvement based on current performance)
      optimizedContactToDiscovery: potentialContactToDiscovery,
      optimizedDiscoveryToPresentation: potentialDiscoveryToPresentation,
      optimizedPresentationToDeal: potentialPresentationToDeal,
      
      // Revenue metrics
      currentRevenue,
      potentialRevenue: realisticRevenue,
      monthlyRevenueGap,
      annualRevenueGap,
      revenueGapSource,
      
      // Potential funnel
      potentialDiscovery: realisticDiscovery,
      potentialPresentations: realisticPresentations,
      potentialDeals: realisticDeals,
      
      // NEW: Rep Performance Metrics (v2)
      repHealthScore: salesRepHealthScore,
      absoluteGoalAttainmentPct,
      expectedRevenueByNow,
      pacingVsPlanPct,
      paceGapToDate,
      projectedYearEndRevenue,
      projectedYearEndGap,
      targetPerRepYear,
      targetPerRepMonth,
      actualRevenuePerRepYTD,
      actualRevenuePerRepMonthPaced,
      repTargetConsistencyGap,
      pacingRecoveryPotentialMonthly,
      repAlignmentPotentialMonthly,
      pipelineActivationPotentialMonthly,
      targetManagementScore,
      pipelineReadinessScore,
      teamExecutionScore,
      
      // NEW: Goal Path - What's needed from NOW to reach year-end target (v2: audit-adjustiert)
      goalPath: {
        remainingRevenueNeeded,
        monthsRemainingApprox,
        requiredMonthlyRevenueFromNow,
        requiredDealsRemaining,
        requiredDealsPerMonthFromNow,
        requiredDealsPerRepPerMonthFromNow,
        requiredPresentationsRemaining,
        requiredPresentationsPerMonthFromNow,
        requiredPresentationsPerRepPerMonthFromNow,
        requiredDiscoveryCallsRemaining,
        requiredDiscoveryCallsPerMonthFromNow,
        requiredDiscoveryCallsPerRepPerMonthFromNow,
        requiredContactsRemaining,
        requiredContactsPerMonthFromNow,
        requiredContactsPerRepPerMonthFromNow,
        currentContactsPerRepPerMonth,
        currentDiscoveryCallsPerRepPerMonth,
        currentPresentationsPerRepPerMonth,
        currentDealsPerRepPerMonth,
        requiredContactToDiscoveryPctAtCurrentVolume,
        requiredDiscoveryToPresentationPctAtCurrentVolume,
        requiredPresentationToDealPctAtCurrentVolume,
        usesFallbackBenchmarks: goalPathUsesFallbackBenchmarks,
        // Aktuelle Conversion-Ratios (für Diagnose)
        currentContactToDiscoveryRatio,
        currentDiscoveryToPresentationRatio,
        currentPresentationToDealRatio,
        // Adjustierte Conversion-Raten (für Vergleich)
        adjustedC2D,
        adjustedD2P,
        adjustedP2D,
        adjustedContactToDiscoveryRatio: adjustedC2DRatio,
        adjustedDiscoveryToPresentationRatio: adjustedD2PRatio,
        adjustedPresentationToDealRatio: adjustedP2DRatio,
        // confidenceScore + confidenceLabel sind direkt unten als finale Felder abgelegt
        // Realistischer Pfad (audit-adjustierte Conversion-Raten, methodisch sauber)
        realisticRequiredPresentationsPerMonthFromNow,
        realisticRequiredDiscoveryCallsPerMonthFromNow,
        realisticRequiredContactsPerMonthFromNow,
        realisticRequiredContactsPerRepPerMonthFromNow,
        realisticRequiredDiscoveryCallsPerRepPerMonthFromNow,
        realisticRequiredPresentationsPerRepPerMonthFromNow,
        confidenceScore: goalPathConfidence.score,
        confidenceLabel: goalPathConfidence.label,
      },

      // Activity Benchmark Signal
      contactActivitySignal,

      // Capacity Check
      capacityCheck: {
        currentMaxRevenueCapacity,
        optimizedMaxRevenueCapacity,
        maxRevenueCapacity: optimizedMaxRevenueCapacity,
        capacityVsTargetPct,
        capacityGap,
        isTargetUnreachableWithCurrentSetup,
        isTargetUnreachableWithCurrentPerformance,
        currentDealsPerRepPerMonth: currentDealsPerRepPerMonthCapacity,
        realisticDealsPerRepPerMonth: optimizedDealsPerRepPerMonthCapacity,
        contactsPerRepPerMonth: Math.round(contactsPerRepPerMonth),
      },

      // Cost Efficiency
      costInefficiency: {
        totalSalaryCostYear,
        totalSalaryCostMonthly,
        inefficientSalaryCostYear,
        performanceRatio,
        revenuePerEuroSalary,
        revenueEfficiencyGapAnnual,
        targetRevenuePerEuroSalary,
        showBudgetEfficiencyWarning: inefficiencyShouldBeShown,
        salaryEfficiencyBand,
      },

      // Forecast confidence signal
      forecastSignal: {
        isEarlyForecastLowConfidence,
        forecastConfidenceLabel,
      },

      // Reverse Funnel — from target revenue back to required pipeline
      reverseFunnel: (() => {
        const reps = Math.max(1, productiveReps);

        // ----- SOLL (Team Total) — operativ belastbarer Pfad (audit-adjustierte Raten, kein doppelter Friction)
        const sollContactsTeam = realisticRequiredContactsPerMonthFromNow;
        const sollDiscoveryTeam = realisticRequiredDiscoveryCallsPerMonthFromNow;
        const sollPresentationsTeam = realisticRequiredPresentationsPerMonthFromNow;
        const sollDealsTeam = requiredDealsPerMonthFromNow;

        // ----- IST (Team Total) — direkt aus Eingabe -----
        const istContactsTeam = currentContacts;        // Gesamteingabe
        const istDiscoveryTeam = currentDiscovery;
        const istPresentationsTeam = currentPresentations;
        const istDealsTeam = currentDeals;

        // ----- Per-Rep Werte -----
        const istContactsPerRep    = reps > 0 ? istContactsTeam    / reps : 0;
        const istDiscoveryPerRep   = reps > 0 ? istDiscoveryTeam   / reps : 0;
        const istPresentationsPerRep = reps > 0 ? istPresentationsTeam / reps : 0;
        const istDealsPerRep       = reps > 0 ? istDealsTeam       / reps : 0;

        const sollContactsPerRep    = reps > 0 ? sollContactsTeam    / reps : 0;
        const sollDiscoveryPerRep   = reps > 0 ? sollDiscoveryTeam   / reps : 0;
        const sollPresentationsPerRep = reps > 0 ? sollPresentationsTeam / reps : 0;
        const sollDealsPerRep       = reps > 0 ? sollDealsTeam       / reps : 0;

        return {
          productiveReps: reps,
          targetMonthlyRevenue: requiredMonthlyRevenueFromNow,
          targetMonthlyRevenuePerRep: reps > 0 ? requiredMonthlyRevenueFromNow / reps : 0,

          // Team Totals
          targetDealsPerMonth:         sollDealsTeam,
          targetPresentationsPerMonth: sollPresentationsTeam,
          targetDiscoveryCallsPerMonth: sollDiscoveryTeam,
          targetContactsPerMonth:      sollContactsTeam,

          // Per-Rep Soll
          targetDealsPerRepPerMonth:         sollDealsPerRep,
          targetPresentationsPerRepPerMonth: sollPresentationsPerRep,
          targetDiscoveryCallsPerRepPerMonth: sollDiscoveryPerRep,
          targetContactsPerRepPerMonth:      sollContactsPerRep,

          // IST Team Totals
          istContactsPerMonth:      istContactsTeam,
          istDiscoveryCallsPerMonth: istDiscoveryTeam,
          istPresentationsPerMonth: istPresentationsTeam,
          istDealsPerMonth:        istDealsTeam,
          istMonthlyRevenue:       currentRevenue,
          istMonthlyRevenuePerRep: reps > 0 ? currentRevenue / reps : 0,

          // IST Per-Rep
          istContactsPerRepPerMonth:      istContactsPerRep,
          istDiscoveryCallsPerRepPerMonth: istDiscoveryPerRep,
          istPresentationsPerRepPerMonth:  istPresentationsPerRep,
          istDealsPerRepPerMonth:          istDealsPerRep,

          // Gaps (team total, positive = need more)
          gapContacts:     Math.max(0, sollContactsTeam     - istContactsTeam),
          gapDiscovery:    Math.max(0, sollDiscoveryTeam    - istDiscoveryTeam),
          gapPresentations: Math.max(0, sollPresentationsTeam - istPresentationsTeam),
          gapDeals:        Math.max(0, sollDealsTeam        - istDealsTeam),
          gapRevenue:      Math.max(0, requiredMonthlyRevenueFromNow - currentRevenue),

          // Gaps per Rep
          gapContactsPerRep:     Math.max(0, sollContactsPerRep     - istContactsPerRep),
          gapDiscoveryPerRep:    Math.max(0, sollDiscoveryPerRep    - istDiscoveryPerRep),
          gapPresentationsPerRep: Math.max(0, sollPresentationsPerRep - istPresentationsPerRep),
          gapDealsPerRep:        Math.max(0, sollDealsPerRep        - istDealsPerRep),

          hasEnoughData:
            currentContacts > 0 &&
            currentDiscovery > 0 &&
            currentPresentations > 0 &&
            currentDeals > 0 &&
            totalRevenueTargetYear > 0,
        };
      })(),

      // Activity Reality Check — operative (realistische) Sollsicht
      activityCheck: {
        actualContactsPerRepPerMonth: currentContactsPerRepPerMonth,
        requiredContactsPerRepFromNow: realisticRequiredContactsPerRepPerMonthFromNow,
        isActivityUnrealistic:
          realisticRequiredContactsPerRepPerMonthFromNow > 0
            ? realisticRequiredContactsPerRepPerMonthFromNow / Math.max(1, currentContactsPerRepPerMonth) > 2.0
            : false,
      },

      // -------------------------------------------------------
      // FUNNEL DIAGNOSIS
      // -------------------------------------------------------
      funnelDiagnosis: {
        stages: [
          {
            name: "Contact → Discovery",
            actual: currentC2D,
            required: requiredContactToDiscoveryPctAtCurrentVolume ?? 0,
            potential: improvedC2D,
            gap: (requiredContactToDiscoveryPctAtCurrentVolume ?? 0) - currentC2D,
            isBlocking: (requiredContactToDiscoveryPctAtCurrentVolume ?? 0) > improvedC2D,
          },
          {
            name: "Discovery → Presentation",
            actual: currentD2P,
            required: requiredDiscoveryToPresentationPctAtCurrentVolume ?? 0,
            potential: improvedD2P,
            gap: (requiredDiscoveryToPresentationPctAtCurrentVolume ?? 0) - currentD2P,
            isBlocking: (requiredDiscoveryToPresentationPctAtCurrentVolume ?? 0) > improvedD2P,
          },
          {
            name: "Presentation → Deal",
            actual: currentP2D,
            required: requiredPresentationToDealPctAtCurrentVolume ?? 0,
            potential: improvedP2D,
            gap: (requiredPresentationToDealPctAtCurrentVolume ?? 0) - currentP2D,
            isBlocking: (requiredPresentationToDealPctAtCurrentVolume ?? 0) > improvedP2D,
          },
        ],
      },

      // -------------------------------------------------------
      // DATA QUALITY SCORE
      // -------------------------------------------------------
      dataQuality: (() => {
        let score = 100;
        const missing: string[] = [];
        if (!currentContacts || currentContacts === 0) { score -= 30; missing.push("Kontakte"); }
        if (!currentDiscovery || currentDiscovery === 0) { score -= 30; missing.push("Discovery Calls"); }
        if (!currentPresentations || currentPresentations === 0) { score -= 20; missing.push("Präsentationen"); }
        if (!currentDeals || currentDeals === 0) { score -= 20; missing.push("Abschlüsse"); }
        return {
          score,
          level: score > 80 ? "high" as const : score > 50 ? "medium" as const : "low" as const,
          missingFields: missing,
        };
      })(),

      // -------------------------------------------------------
      // CONVERSION SIMULATOR BASE
      // -------------------------------------------------------
      conversionSimulator: {
        baseContacts: currentContacts,
        baseC2D: currentC2D,
        baseD2P: currentD2P,
        baseP2D: currentP2D,
        avgDealSize,
        currentMonthlyRevenue: currentRevenue,
      },

      // Sales Cycle Warning
      salesCycleWarning: {
        salesCycleMonths,
        isSalesCycleTooLong,
        dealsUnreachableDueToSalesCycle,
        monthsRemainingApprox,
      },
    },
    
    // NEW: Team Performance (detailed rep-level data)
    teamPerformance: {
      productiveReps,
      totalRevenueTargetYear,
      revenueAchievedYTD,
      expectedRevenueByNow,
      absoluteGoalAttainmentPct,
      pacingVsPlanPct,
      paceGapToDate,
      projectedYearEndRevenue,
      projectedYearEndGap,
      targetPerRepYear,
      targetPerRepMonth,
      actualRevenuePerRepYTD,
      actualRevenuePerRepMonthPaced,
      targetManagementScore,
      pipelineReadinessScore,
      teamExecutionScore,
      repHealthScore: salesRepHealthScore,
      pacingRecoveryPotentialMonthly,
      repAlignmentPotentialMonthly,
      pipelineActivationPotentialMonthly,
      summarySignals,
      contactActivitySignal,
    },
    
    
    
    views: {
      executiveSummary: {
        title: "Executive Summary",
        mainMessage:
          summarySignals.hasPacingProblem
            ? "Der größte Hebel liegt aktuell darin, die Sales Pace auf Zielkurs zu bringen. Das Team liegt unter dem linearen Sollverlauf für das Jahresziel."
            : summarySignals.hasTargetBreakdownProblem
            ? "Das Umsatzziel ist noch nicht sauber auf operative Verantwortlichkeiten heruntergebrochen. Das erschwert Steuerung und Review."
            : summarySignals.hasPipelineProblem
            ? "Die größten Umsatzverluste entstehen durch unzureichend belastbare Pipeline pro Rep. Dadurch entsteht ein strukturelles Risiko für die Zielerreichung."
            : "Die größten Hebel liegen in Prozessqualität, Disziplin und Steuerbarkeit.",
      },
      hero: {
        title: "Realistisches Umsatzpotenzial",
        monthlyRevenueGap,
        annualRevenueGap,
        currentRevenue,
        realisticRevenue,
        conservativeRevenue,
        ambitiousRevenue,
        additionalRevenuePerMonth: monthlyRevenueGap,
        rangeLabel: `${formatCurrencyCompact(conservativeRevenue - currentRevenue)} bis ${formatCurrencyCompact(ambitiousRevenue - currentRevenue)} / Monat`,
        confidenceScore: completenessScore,
      },
      benchmarkView: null,
      riskView: {
        topRisks: [],
        allRisks: [],
      },
      leverView: {
        topLevers: augmentedTopLevers.slice(0, 3),
        allLevers: augmentedTopLevers,
      },
      recommendationView: {
        topPriorities: [],
        quickWins: [],
        systemFixes: [],
        structuralFixes: [],
        roadmap: {
          first30Days: [],
          days31to60: [],
          days61to90: [],
        },
      },
      auditMeta: {
        completenessScore,
        answeredQuestions,
        totalQuestions,
        unknownFields,
      },
    },
  };
}